// src/pages/TicketDetail.jsx
// Rediseño visual aplicado 2026-05-13. Lógica preservada 1:1 desde la versión previa.
import React, { useEffect, useState, useContext, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Form, Button, Spinner, Dropdown, ButtonGroup, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

import api from '../services/api';
import socket from '../services/socket';
import { AuthContext } from '../context/AuthContext';
import { playCommentSound } from '../utils/sound';
import useHotkeys from '../hooks/useHotkeys';
import ImageUploader, { AttachmentGrid } from '../components/ImageUploader';
import SLAChip from '../components/ui/SLAChip';
import DeliveryCalendar from '../components/ui/DeliveryCalendar';
import UserProfileModal from '../components/UserProfileModal';
import { absoluteTime } from '../utils/relativeTime';
import { nombreCorto, nombreVisible } from '../utils/nombreCorto';
import Copyable from '../components/ui/Copyable';
import Skeleton from '../components/Skeleton';


// ══════════════════════════════════════════════════════════════════════════════
// Iconos SVG inline (lucide-style) — sin dependencia nueva
// ══════════════════════════════════════════════════════════════════════════════
const Icon = ({ children, size = 16, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    back:      <Icon><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></Icon>,
    clock:     <Icon><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>,
    building:  <Icon><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01" /></Icon>,
    flag:      <Icon><path d="M4 22V4" /><path d="M4 4h13l-2 4 2 4H4" /></Icon>,
    send:      <Icon><path d="m22 2-7 20-4-9-9-4 20-7Z" /></Icon>,
    lock:      <Icon><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></Icon>,
    arrowUp:   <Icon><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></Icon>,
    move:      <Icon><path d="m5 9 4-4 4 4" /><path d="M9 5v14" /><path d="m19 15-4 4-4-4" /><path d="M15 19V5" /></Icon>,
    check:     <Icon><path d="M20 6 9 17l-5-5" /></Icon>,
    x:         <Icon><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>,
    zap:       <Icon><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" /></Icon>,
    plus:      <Icon><path d="M12 5v14" /><path d="M5 12h14" /></Icon>,
    pencil:    <Icon><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" /></Icon>,
    trash:     <Icon><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></Icon>,
    mail:      <Icon><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" /></Icon>,
    shield:    <Icon><path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5l-8-3Z" /></Icon>,
    chevron:   <Icon><path d="m6 9 6 6 6-6" /></Icon>,
    chevronUp: <Icon><path d="m18 15-6-6-6 6" /></Icon>,
    sparkles:  <Icon><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" /></Icon>,
    paperclip: <Icon><path d="m21 12-9.5 9.5a5 5 0 0 1-7-7L14 4a3.5 3.5 0 0 1 5 5L9 19" /></Icon>,
    package:   <Icon><path d="M16.5 9.4 7.55 4.24" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.27 6.96 8.73 5.05 8.73-5.05" /><path d="M12 22.08V12" /></Icon>,
    calendar:  <Icon><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></Icon>,
    arrowDown: <Icon><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></Icon>,
};


// ══════════════════════════════════════════════════════════════════════════════
// Configuraciones de estado / prioridad / autorización
// ══════════════════════════════════════════════════════════════════════════════
const STATUS_CFG = {
    open:        { label: 'Abierto',     color: '#1d4ed8', bg: 'var(--c-status-open-bg)',     dot: '#2563eb' },
    in_progress: { label: 'En progreso', color: '#b45309', bg: 'var(--c-status-progress-bg)', dot: '#d97706' },
    closed:      { label: 'Resuelto',    color: '#059669', bg: 'var(--c-priority-low-bg)',     dot: '#059669', check: true },
};
const PRIORITY_CFG = {
    low:    { label: 'Baja',    color: '#047857', bg: 'var(--c-priority-low-bg)' },
    medium: { label: 'Media',   color: '#1d4ed8', bg: 'var(--c-status-open-bg)' },
    high:   { label: 'Alta',    color: '#b45309', bg: 'var(--c-status-progress-bg)' },
    urgent: { label: 'Urgente', color: '#b91c1c', bg: 'var(--c-priority-urgent-bg)' },
};
const AUTH_STATUS_LABEL = { none: 'Sin autorización', pending: 'Pendiente', approved: 'Aprobado', rejected: 'Rechazado' };


// ══════════════════════════════════════════════════════════════════════════════
// Helpers visuales
// ══════════════════════════════════════════════════════════════════════════════
function AuthBadge({ status }) {
    const cls = {
        none:     'td-auth-none',
        pending:  'td-auth-pending',
        approved: 'td-auth-approved',
        rejected: 'td-auth-rejected',
    }[status] || 'td-auth-none';
    return <span className={`td-auth-badge ${cls}`}>{AUTH_STATUS_LABEL[status] || status}</span>;
}

function fmt(date) {
    if (!date) return null;
    return new Date(date).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
}

function UndoToast({ message, onUndo }) {
    const [done, setDone] = React.useState(false);
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ flex: 1 }}>{message}</span>
            {!done && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setDone(true);
                        onUndo();
                    }}
                    style={{
                        background: 'transparent',
                        border: '1px solid currentColor',
                        color: 'inherit',
                        borderRadius: 6,
                        padding: '2px 10px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                    }}
                >
                    Deshacer
                </button>
            )}
        </div>
    );
}

function StarRating({ value, onChange, readOnly = false, size = 28 }) {
    const [hovered, setHovered] = React.useState(0);
    return (
        <div style={{ display: 'flex', gap: 4 }}>
            {[1, 2, 3, 4, 5].map(s => (
                <span
                    key={s}
                    style={{
                        fontSize: size,
                        cursor: readOnly ? 'default' : 'pointer',
                        color: (hovered || value) >= s ? '#f59e0b' : '#d1d5db',
                        transition: 'color 0.1s',
                        lineHeight: 1,
                        userSelect: 'none',
                    }}
                    onClick={() => !readOnly && onChange && onChange(s)}
                    onMouseEnter={() => !readOnly && setHovered(s)}
                    onMouseLeave={() => !readOnly && setHovered(0)}
                >
                    ★
                </span>
            ))}
        </div>
    );
}

function resolveAuth1Status(ticket) {
    if (ticket.authorizerStatus) return ticket.authorizerStatus;
    if (!ticket.authorizer2 &&
        (ticket.authorizationStatus === 'approved' || ticket.authorizationStatus === 'rejected')) {
        return ticket.authorizationStatus;
    }
    return 'pending';
}

const AVATAR_PALETTE = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ec4899','#06b6d4','#ef4444','#84cc16'];
function nameToColor(name) {
    if (!name) return '#9ca3af';
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}
function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[1][0]).toUpperCase();
}
function Avatar({ name, size = 34, ring = false }) {
    return (
        <div style={{
            width: size, height: size, borderRadius: '50%',
            background: nameToColor(name), color: '#fff',
            fontWeight: 700, fontSize: size * 0.36,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, letterSpacing: 0.3,
            boxShadow: ring ? '0 0 0 3px var(--c-card), 0 0 0 4px var(--c-border)' : 'none',
        }}>
            {getInitials(name)}
        </div>
    );
}

function UserNameButton({ userId, name, onClick, style }) {
    if (!userId || !onClick) {
        return <span style={style}>{name || '—'}</span>;
    }
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClick(userId); }}
            title="Ver perfil"
            className="td-user-link"
            style={style}
        >
            {name}
        </button>
    );
}


// ══════════════════════════════════════════════════════════════════════════════
// Activity timeline helpers
// ══════════════════════════════════════════════════════════════════════════════
const ACTION_ICON_NODE = {
    created:                  <Icon size={14}><path d="M15 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18 2l4 4-10 10H8v-4L18 2z" /></Icon>,
    status_changed:           <Icon size={14}><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></Icon>,
    priority_changed:         <Icon size={14}><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" /></Icon>,
    assigned:                 <Icon size={14}><circle cx="12" cy="8" r="4" /><path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" /></Icon>,
    escalated:                <Icon size={14}><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></Icon>,
    authorization_configured: <Icon size={14}><path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5l-8-3Z" /></Icon>,
    authorization_decided:    <Icon size={14}><path d="M20 6 9 17l-5-5" /></Icon>,
    comment_added:            <Icon size={14}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></Icon>,
    comment_deleted:          <Icon size={14}><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></Icon>,
    department_moved:         <Icon size={14}><path d="M5 9 9 5l4 4" /><path d="M9 5v14" /></Icon>,
};
const ACTION_BG = {
    created:                  '#dbeafe',
    status_changed:           '#fef9c3',
    priority_changed:         '#ffedd5',
    assigned:                 '#dcfce7',
    escalated:                '#fee2e2',
    authorization_configured: '#ede9fe',
    authorization_decided:    '#d1fae5',
    comment_added:            '#f0fdf4',
    comment_deleted:          '#fee2e2',
    department_moved:         '#e0f2fe',
};
const ACTION_COLOR = {
    created:                  '#1d4ed8',
    status_changed:           '#a16207',
    priority_changed:         '#c2410c',
    assigned:                 '#15803d',
    escalated:                '#b91c1c',
    authorization_configured: '#6d28d9',
    authorization_decided:    '#047857',
    comment_added:            '#15803d',
    comment_deleted:          '#b91c1c',
    department_moved:         '#0369a1',
};
const STATUS_LABEL_ACT = { open: 'Abierto', in_progress: 'En progreso', closed: 'Cerrado' };
const PRIORITY_LABEL_ACT = { low: 'Baja', medium: 'Media', high: 'Alta', urgent: 'Urgente' };
function formatActivity(log, viewerRole) {
    const d = log.details || {};
    // Los nombres en details (asignado/reasignado) son de agentes: al solicitante
    // se le muestran acortados.
    const nv = (n) => nombreVisible(n, viewerRole);
    switch (log.action) {
        case 'created':                  return `Ticket creado: "${d.title || ''}"`;
        case 'status_changed':           return `Estado cambiado de "${STATUS_LABEL_ACT[d.from] || d.from}" a "${STATUS_LABEL_ACT[d.to] || d.to}"`;
        case 'priority_changed':         return `Prioridad cambiada de "${PRIORITY_LABEL_ACT[d.from] || d.from}" a "${PRIORITY_LABEL_ACT[d.to] || d.to}"`;
        case 'assigned':                 return d.auto ? `Autoasignado a ${nv(d.to) || 'agente'}` : `Asignado a ${nv(d.to) || 'agente'}`;
        case 'reassigned':               return `Reasignado de ${nv(d.from) || '?'} a ${nv(d.to) || '?'}`;
        case 'escalated':                return `Escalado de "${PRIORITY_LABEL_ACT[d.from] || d.from}" a "${PRIORITY_LABEL_ACT[d.to] || d.to}"`;
        case 'authorization_configured': return d.enabled ? 'Autorización activada' : 'Autorización desactivada';
        case 'authorization_decided':    return `Decisión de autorización: ${d.decision === 'approved' ? 'Aprobado' : 'Rechazado'}`;
        case 'comment_added':            return d.excerpt ? `Comentario: "${d.excerpt}${d.excerpt.length >= 80 ? '…' : ''}"` : 'Comentario agregado';
        case 'comment_deleted':          return `Comentario borrado${d.excerpt ? `: "${d.excerpt}${d.excerpt.length >= 80 ? '…' : ''}"` : ''}${d.reason ? ` — razón: ${d.reason}` : ''}`;
        case 'department_moved':         return `Ticket redirigido a "${d.to || 'otro departamento'}"`;
        default:                         return log.action;
    }
}

function StatusPill({ status }) {
    const cfg = STATUS_CFG[status] || { label: status, color: 'var(--c-text-3)', bg: 'var(--c-status-closed-bg)', dot: 'var(--c-text-3)' };
    return (
        <span className="td-pill" style={{ background: cfg.bg, color: cfg.color }}>
            {cfg.check
                ? <span style={{ fontSize: 11, fontWeight: 800, lineHeight: 1 }}>✓</span>
                : <span className="td-pill-dot" style={{ background: cfg.dot }} />}
            {cfg.label}
        </span>
    );
}
function PriorityPill({ priority }) {
    const cfg = PRIORITY_CFG[priority] || { label: priority, color: 'var(--c-text-3)', bg: 'var(--c-status-closed-bg)' };
    return (
        <span className="td-pill" style={{ background: cfg.bg, color: cfg.color }}>
            {cfg.label}
        </span>
    );
}


// ══════════════════════════════════════════════════════════════════════════════
// Scoped CSS
// ══════════════════════════════════════════════════════════════════════════════
const TD_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.td-root {
    --td-accent: var(--brand-700, #0a4aa8);
    --td-accent-hover: var(--brand-800, #07357f);
    --td-accent-light: #dbeafe;
    --td-accent-ring: rgba(29, 78, 216, .12);
    --td-radius-lg: 14px;
    --td-radius-md: 10px;
    --td-radius-sm: 8px;
    --td-shadow-sm: 0 1px 2px rgba(15,23,42,.04), 0 2px 8px rgba(15,23,42,.04);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    background: var(--c-bg);
    min-height: 100vh;
    padding: 20px 28px 60px;
    color: var(--c-text-1);
}
.td-root h1, .td-root h2, .td-root h3 { letter-spacing: -0.01em; margin: 0; }
.td-root * { box-sizing: border-box; }

/* Breadcrumb */
.td-breadcrumb {
    max-width: 1380px; margin: 0 auto 14px;
    display: flex; align-items: center; gap: 10px;
    font-size: 12px; color: var(--c-text-3);
}
.td-breadcrumb a {
    color: var(--c-text-3); text-decoration: none;
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 8px; border-radius: 6px;
    transition: all .15s;
}
.td-breadcrumb a:hover { background: var(--c-msg-bg); color: var(--c-text-1); }
.td-breadcrumb .td-folio {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-weight: 600; color: var(--c-text-2);
    background: var(--c-msg-bg); padding: 3px 10px; border-radius: 6px;
    font-size: 11px; letter-spacing: 0.4px;
}

/* Layout */
.td-layout {
    max-width: 1380px; margin: 0 auto;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 340px;
    gap: 20px;
    align-items: start;
}
.td-main { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.td-sidebar { display: flex; flex-direction: column; gap: 12px; position: sticky; top: 76px; }

/* Card */
.td-card {
    background: var(--c-card);
    border: 1px solid var(--c-border);
    border-radius: var(--td-radius-lg);
    box-shadow: var(--td-shadow-sm);
}
.td-card-pad { padding: 20px 24px; }
.td-card-pad-sm { padding: 16px 18px; }

.td-label {
    font-size: 11px; font-weight: 600; letter-spacing: 0.6px;
    text-transform: uppercase; color: var(--c-text-4); margin-bottom: 10px;
}
.td-label-inline { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }

/* Pills */
.td-pill {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 11px; border-radius: 999px;
    font-weight: 600; font-size: 12px; line-height: 1.4;
    letter-spacing: 0.1px;
}
.td-pill-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.td-pill-neutral { background: var(--c-msg-bg); color: var(--c-text-2); }
.td-pill-country { background: #ecfdf5; color: #047857; }

/* Auth badges */
.td-auth-badge {
    display: inline-flex; align-items: center;
    padding: 2px 9px; border-radius: 999px;
    font-size: 11px; font-weight: 600;
}
.td-auth-none      { background: var(--c-status-closed-bg); color: var(--c-text-3); }
.td-auth-pending   { background: var(--c-status-progress-bg); color: #b45309; }
.td-auth-approved  { background: var(--c-priority-low-bg); color: #047857; }
.td-auth-rejected  { background: var(--c-priority-urgent-bg); color: #b91c1c; }

/* Header */
.td-header-title {
    font-size: 24px; font-weight: 700; line-height: 1.25;
    color: var(--c-text-1); margin: 0 0 14px;
    cursor: default;
}
.td-header-title.editable { cursor: pointer; }
.td-header-title.editable:hover { color: var(--td-accent); }
/* Número de ticket grande junto al título */
.td-header-folio-big {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 28px; font-weight: 800; color: var(--td-accent);
    margin-right: 12px; letter-spacing: 0.5px; cursor: default;
}
.td-title-edit { display: flex; gap: 8px; margin-bottom: 14px; align-items: stretch; }
.td-title-input {
    flex: 1; font-weight: 700; font-size: 20px; line-height: 1.35;
    color: var(--c-text-1); background: var(--c-input-bg);
    border: 1px solid var(--c-border); border-radius: var(--td-radius-sm);
    padding: 6px 12px; font-family: inherit; outline: none;
}
.td-title-input:focus { border-color: var(--td-accent); box-shadow: 0 0 0 3px var(--td-accent-ring); }

.td-pills-row {
    display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; align-items: center;
}

.td-meta-row {
    display: flex; flex-wrap: wrap; gap: 6px 22px;
    padding-top: 14px; border-top: 1px solid var(--c-card-header-border);
    font-size: 12px; color: var(--c-text-3);
}
.td-meta-row > span { display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.td-meta-row strong { color: var(--c-text-2); font-weight: 600; }
.td-meta-row .td-meta-label { color: var(--c-text-4); }
.td-meta-row .sla-warn { color: #b45309; }

.td-tag {
    display: inline-flex; align-items: center;
    font-size: 10px; font-weight: 600;
    background: var(--c-msg-bg); color: var(--c-text-3);
    border: 1px solid var(--c-border);
    border-radius: 4px; padding: 1px 7px;
}
.td-tag-strong {
    background: var(--td-accent); color: #fff; border: none;
    text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700;
}
.td-tag-country {
    background: #ecfdf5; color: #047857; border-color: #a7f3d0;
}

/* Description */
.td-desc {
    margin: 0; white-space: pre-wrap;
    font-size: 14.5px; line-height: 1.7; color: var(--c-text-2);
}

/* Materials grid */
.td-materials-grid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 10px;
}
.td-material {
    border: 1px solid var(--c-border); border-radius: var(--td-radius-md);
    padding: 10px 12px; background: var(--c-input-bg);
}
.td-material-name { font-size: 13px; font-weight: 600; color: var(--c-text-1); margin: 0 0 2px; }
.td-material-meta { font-size: 11px; color: var(--c-text-3); }
.td-materials-total {
    margin-top: 12px; padding-top: 12px;
    border-top: 1px solid var(--c-card-header-border);
    display: flex; justify-content: space-between; font-size: 13px;
}
.td-materials-total strong { color: var(--c-text-1); }
.td-material-single {
    display: flex; align-items: center; gap: 14px;
}
.td-material-single .num { font-size: 22px; font-weight: 800; color: var(--c-text-1); line-height: 1; }

/* Message thread */
.td-thread { display: flex; flex-direction: column; gap: 18px; margin-top: 4px; }
.td-msg { display: flex; gap: 12px; }
.td-msg.mine { flex-direction: row-reverse; }
.td-msg .body-wrap { max-width: 78%; min-width: 0; }
.td-msg .author-row {
    display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap;
    margin-bottom: 6px;
}
.td-msg.mine .author-row { flex-direction: row-reverse; }
.td-msg .author { font-weight: 600; font-size: 13px; color: var(--c-text-1); }
.td-msg .time { font-size: 11px; color: var(--c-text-4); }
.td-msg .bubble {
    background: var(--c-msg-bg); color: var(--c-text-1);
    padding: 12px 16px; border-radius: 14px; font-size: 14px; line-height: 1.55;
    box-shadow: 0 1px 2px rgba(0,0,0,.04);
    border-top-left-radius: 4px;
    word-wrap: break-word; overflow-wrap: break-word;
}
.td-msg.mine .bubble {
    background: var(--td-accent); color: #fff;
    border-top-right-radius: 4px; border-top-left-radius: 14px;
}
.td-msg .bubble p { margin: 0; white-space: pre-wrap; }
.td-msg.internal .bubble {
    background: rgba(245, 158, 11, 0.12); color: var(--c-text-1);
    border: 1px solid #fde68a; border-left: 3px solid #f59e0b;
}
.td-msg.mine.internal .bubble {
    background: rgba(245, 158, 11, 0.22); color: var(--c-text-1);
    border: 1px solid #fde68a; border-right: 3px solid #f59e0b; border-left: 1px solid #fde68a;
}
.td-role-badge {
    font-size: 10px; font-weight: 700; color: #fff;
    border-radius: 4px; padding: 1px 6px;
    letter-spacing: 0.5px; text-transform: uppercase;
}
.td-role-admin   { background: #1e3a5f; }
.td-role-agent   { background: #14532d; }
.td-role-leader  { background: #3b0764; }
.td-internal-badge {
    font-size: 10px; font-weight: 700;
    background: #f59e0b; color: #fff;
    border-radius: 4px; padding: 2px 7px;
    letter-spacing: 0.5px; text-transform: uppercase;
    display: inline-flex; align-items: center; gap: 4px;
}
.td-comment-delete {
    background: none; border: none; color: #dc2626;
    font-size: 11px; cursor: pointer; padding: 2px 6px; border-radius: 4px;
    margin-left: auto; display: inline-flex; align-items: center; gap: 4px;
}
.td-comment-delete:hover { background: var(--c-priority-urgent-bg); }

.td-system {
    display: flex; align-items: center; gap: 10px;
    color: var(--c-text-4); font-size: 12px;
}
.td-system .line { flex: 1; height: 1px; background: var(--c-border); }
.td-system .text { display: inline-flex; align-items: center; gap: 6px; padding: 0 4px; }

.td-empty-conv { color: var(--c-text-4); font-size: 13px; text-align: center; margin: 24px 0; }

/* Composer */
.td-composer {
    margin-top: 22px;
    border: 1.5px solid var(--c-border);
    border-radius: var(--td-radius-lg);
    background: var(--c-input-bg);
    transition: border-color .2s ease, background .2s ease;
    position: relative;
}
.td-composer.internal {
    border-color: #f59e0b;
    background: var(--c-status-progress-bg);
}
.td-composer textarea {
    width: 100%; border: none; outline: none; resize: vertical;
    background: transparent; color: var(--c-text-1);
    padding: 14px 16px; font-size: 14px; line-height: 1.6;
    font-family: inherit; min-height: 90px;
    border-top-left-radius: var(--td-radius-lg);
    border-top-right-radius: var(--td-radius-lg);
    display: block;
}
.td-composer-uploader {
    padding: 0 12px 8px;
    border-top: 1px solid var(--c-card-header-border);
    padding-top: 10px;
}
.td-composer-bar {
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px; flex-wrap: wrap;
    padding: 8px 12px; border-top: 1px solid var(--c-border);
    background: var(--c-card);
    border-bottom-left-radius: var(--td-radius-lg);
    border-bottom-right-radius: var(--td-radius-lg);
}
.td-composer-bar-left {
    display: flex; gap: 8px; flex-wrap: wrap; align-items: center;
}
.td-composer-bar-right {
    display: flex; gap: 8px; align-items: center; flex-shrink: 0;
}
.td-composer-icon-btn {
    background: transparent; border: 1px solid var(--c-border);
    color: var(--c-text-2); padding: 6px 10px; border-radius: 8px;
    cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
    font-size: 12px; font-weight: 600; transition: all .15s ease;
    font-family: inherit;
}
.td-composer-icon-btn:hover { background: var(--c-msg-bg); border-color: var(--c-text-3); }
.td-internal-toggle {
    background: transparent; border: 1px solid var(--c-border);
    color: var(--c-text-2); padding: 6px 10px; border-radius: 8px;
    cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
    font-size: 12px; font-weight: 600; transition: all .15s ease;
    font-family: inherit;
}
.td-internal-toggle.on {
    background: #f59e0b; color: #fff; border-color: #f59e0b;
}

.td-canned-dropdown {
    position: absolute; bottom: calc(100% + 6px); left: 12px; z-index: 200;
    width: min(360px, calc(100% - 24px)); max-height: 300px; overflow-y: auto;
    background: var(--c-card); border: 1px solid var(--c-border);
    border-radius: var(--td-radius-md);
    box-shadow: 0 -8px 24px rgba(0,0,0,.12), 0 2px 8px rgba(0,0,0,.06);
}
.td-canned-search {
    width: 100%; padding: 8px 12px; font-size: 13px;
    border: none; border-bottom: 1px solid var(--c-card-header-border);
    background: var(--c-input-bg); color: var(--c-text-1); outline: none;
    font-family: inherit;
}
.td-canned-item {
    padding: 9px 14px; cursor: pointer;
    border-bottom: 1px solid var(--c-card-header-border);
    font-size: 13px; transition: background .15s;
}
.td-canned-item:hover { background: var(--c-msg-bg); }
.td-canned-item .title { font-weight: 600; color: var(--c-text-1); font-size: 12px; margin: 0; }
.td-canned-item .body { color: var(--c-text-3); font-size: 11px; margin: 2px 0 0; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }

/* Buttons (custom) */
.td-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    border: 1px solid transparent; border-radius: var(--td-radius-sm);
    padding: 7px 14px; font-size: 13px; font-weight: 600;
    cursor: pointer; transition: all .15s ease; font-family: inherit;
    text-decoration: none;
}
.td-btn.full { width: 100%; }
.td-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.td-btn-primary { background: var(--td-accent); color: #fff; border-color: var(--td-accent); }
.td-btn-primary:hover:not(:disabled) { background: var(--td-accent-hover); border-color: var(--td-accent-hover); }
.td-btn-soft { background: var(--c-input-bg); color: var(--c-text-1); border-color: var(--c-border); }
.td-btn-soft:hover:not(:disabled) { background: var(--c-msg-bg); border-color: var(--c-text-3); }
.td-btn-success { background: #059669; color: #fff; border-color: #059669; }
.td-btn-success:hover:not(:disabled) { background: #047857; border-color: #047857; }
.td-btn-danger { background: #dc2626; color: #fff; border-color: #dc2626; }
.td-btn-danger:hover:not(:disabled) { background: #b91c1c; border-color: #b91c1c; }
.td-btn-ghost { background: transparent; color: var(--c-text-2); border-color: var(--c-border); }
.td-btn-ghost:hover:not(:disabled) { background: var(--c-msg-bg); }
.td-btn-info { background: #0ea5e9; color: #fff; border-color: #0ea5e9; }
.td-btn-info:hover:not(:disabled) { background: #0284c7; border-color: #0284c7; }

/* Inputs (custom) */
.td-select, .td-input {
    width: 100%; padding: 8px 11px; font-size: 13px;
    border: 1px solid var(--c-border); border-radius: var(--td-radius-sm);
    background: var(--c-input-bg); color: var(--c-text-1);
    font-family: inherit; outline: none;
    transition: border-color .15s, box-shadow .15s;
}
.td-select:focus, .td-input:focus {
    border-color: var(--td-accent); box-shadow: 0 0 0 3px var(--td-accent-ring);
}

/* Sidebar action rows */
.td-action-list { display: flex; flex-direction: column; gap: 4px; }
.td-action-row {
    display: flex; align-items: center; gap: 10px;
    width: 100%; padding: 9px 12px; border-radius: var(--td-radius-sm);
    border: none; background: transparent;
    color: var(--c-text-2); font-size: 13px; font-weight: 500;
    cursor: pointer; text-align: left; font-family: inherit;
    transition: all .15s;
}
.td-action-row:hover:not(:disabled) { background: var(--c-msg-bg); color: var(--c-text-1); }
.td-action-row:disabled { opacity: 0.5; cursor: not-allowed; }
.td-action-row.danger:hover:not(:disabled) { background: var(--c-priority-urgent-bg); color: #b91c1c; }

/* Assignee row */
.td-assignee {
    display: flex; align-items: center; gap: 10px; margin-bottom: 12px;
}
.td-assignee .name { font-size: 13px; font-weight: 600; color: var(--c-text-1); }
.td-assignee .role { font-size: 11px; color: var(--c-text-3); }

.td-collab-stack { display: flex; }
.td-collab-stack > div { margin-left: -8px; }
.td-collab-stack > div:first-child { margin-left: 0; }

.td-collab-row {
    display: flex; align-items: center; gap: 8px;
    padding: 6px 4px; border-radius: 6px;
}
.td-collab-row .remove {
    background: none; border: none; color: #dc2626;
    cursor: pointer; padding: 4px; border-radius: 4px;
    display: inline-flex; align-items: center;
    transition: background .15s;
}
.td-collab-row .remove:hover { background: var(--c-priority-urgent-bg); }

/* User link button */
.td-user-link {
    background: none; border: none; padding: 0; margin: 0;
    color: inherit; font: inherit; cursor: pointer;
    text-decoration: none; border-bottom: 1px dashed currentColor;
    display: inline; transition: color .15s;
}
.td-user-link:hover { color: var(--td-accent); }

/* CC list */
.td-cc-item {
    display: flex; align-items: center; justify-content: space-between;
    background: var(--c-msg-bg); border: 1px solid var(--c-border);
    border-radius: 6px; padding: 5px 10px; margin-bottom: 6px; font-size: 12px;
}
.td-cc-item .email { word-break: break-all; display: inline-flex; align-items: center; gap: 6px; }
.td-cc-item .remove {
    background: none; border: none; color: #dc2626;
    cursor: pointer; font-size: 14px; padding: 0 0 0 8px; line-height: 1;
}

/* Authorizer panel */
.td-auth-panel {
    border-color: #93c5fd !important;
}
.td-auth-row {
    display: flex; align-items: center; gap: 8px;
    font-size: 12px; color: var(--c-text-3);
    margin-bottom: 6px;
}

/* Authorizer status block (config view) */
.td-auth-summary {
    background: var(--c-msg-bg); border-radius: var(--td-radius-sm);
    padding: 10px 12px; margin-bottom: 12px;
    border: 1px solid var(--c-border); font-size: 12px;
}
.td-auth-summary .row-item { color: var(--c-text-3); margin-bottom: 4px; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.td-auth-summary .row-item:last-child { margin-bottom: 0; }

/* Section divider in cards */
.td-card-sep { height: 1px; background: var(--c-card-header-border); margin: 14px 0; }

/* Activity log */
.td-activity-toggle {
    width: 100%; background: none; border: none;
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 24px; cursor: pointer;
    font-family: inherit;
}
.td-activity-toggle:hover { background: var(--c-msg-bg); }
.td-activity-toggle .label { color: var(--c-text-4); font-size: 11px; font-weight: 600; letter-spacing: 0.6px; text-transform: uppercase; display: inline-flex; align-items: center; gap: 8px; }
.td-activity-toggle .label .count { color: var(--c-text-4); font-weight: 500; }
.td-activity-toggle .indicator { font-size: 12px; color: var(--c-text-4); display: inline-flex; align-items: center; gap: 4px; }
.td-activity-body { padding: 16px 24px 6px; }
.td-activity-item { display: flex; gap: 12px; position: relative; }
.td-activity-item .line {
    position: absolute; left: 14px; top: 32px; bottom: -8px;
    width: 2px; background: var(--c-border);
}
.td-activity-item .dot {
    width: 30px; height: 30px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; z-index: 1;
}
.td-activity-item .body { flex: 1; padding-bottom: 16px; }
.td-activity-item .body p { margin: 0; }
.td-activity-item .text { font-size: 13px; color: var(--c-text-1); }
.td-activity-item .meta { font-size: 11px; color: var(--c-text-4); }

/* Floating drawer tab */
.td-drawer-tab {
    position: fixed; top: 50%; transform: translateY(-50%);
    transition: right 0.3s ease;
    z-index: 1050;
    /* Fallback: este botón se renderiza fuera de .td-root, donde no existe --td-accent */
    background: var(--td-accent, var(--brand-700, #0a4aa8)); color: #fff;
    border-radius: 10px 0 0 10px;
    padding: 18px 8px; cursor: pointer;
    box-shadow: -3px 2px 14px rgba(0,0,0,.25);
    user-select: none; writing-mode: vertical-rl;
    font-size: 11px; font-weight: 700; letter-spacing: 1px;
    border: none;
}
.td-drawer-tab:hover { background: var(--td-accent-hover, var(--brand-800, #07357f)); }

.td-drawer {
    position: fixed; right: 0; top: 0; bottom: 0; width: 320px;
    transition: transform 0.3s ease;
    z-index: 1040;
    background: var(--c-card);
    border-left: 1px solid var(--c-border);
    box-shadow: -6px 0 24px rgba(0,0,0,.12);
    display: flex; flex-direction: column;
}
.td-drawer-header {
    padding: 16px 18px;
    border-bottom: 1px solid var(--c-card-header-border);
    display: flex; justify-content: space-between; align-items: center;
    flex-shrink: 0;
}
.td-drawer-header h6 { margin: 0; font-weight: 700; font-size: 14px; color: var(--c-text-1); }
.td-drawer-header .count { margin: 0; font-size: 11px; color: var(--c-text-3); }
.td-drawer-close {
    background: none; border: none; cursor: pointer;
    color: var(--c-text-3); padding: 4px; line-height: 1;
    border-radius: 6px; display: inline-flex; align-items: center;
}
.td-drawer-close:hover { background: var(--c-msg-bg); }

.td-drawer-body { overflow-y: auto; flex: 1; padding: 10px; }
.td-drawer-item {
    padding: 10px 12px; border-radius: var(--td-radius-sm);
    margin-bottom: 6px;
    background: var(--c-bg); cursor: pointer;
    transition: background .15s, border-color .15s;
}
.td-drawer-item:hover { background: var(--c-msg-bg); }
.td-drawer-item.current {
    background: var(--c-status-open-bg);
    box-shadow: 0 0 0 2px var(--td-accent) inset;
}
.td-drawer-item .top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.td-drawer-item .folio {
    font-size: 10px; font-weight: 700;
    background: var(--c-msg-bg); color: var(--c-text-2);
    border-radius: 4px; padding: 1px 6px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.td-drawer-item .title-text {
    margin: 0; font-size: 12px; font-weight: 600; color: var(--c-text-1);
    line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2;
    -webkit-box-orient: vertical; overflow: hidden;
}
.td-drawer-item .dept { margin: 4px 0 0; font-size: 11px; color: var(--c-text-3); display: inline-flex; align-items: center; gap: 4px; }

/* CSAT box (in-page) */
.td-csat-card {
    border-color: #fde68a !important;
}

/* Modal overrides (react-bootstrap modals respect var(--c-card)) */
.modal-content { background: var(--c-card) !important; border: 1px solid var(--c-border) !important; }

/* Fondo oscuro del cajón de tickets (se activa solo en móvil) */
.td-drawer-backdrop { display: none; }
/* Toggle del panel de acciones (solo móvil) */
.td-sidebar-toggle { display: none; }

/* Responsive */
@media (max-width: 920px) {
    .td-layout { grid-template-columns: 1fr; }
    .td-root { padding: 16px 14px 60px; }
    .td-header-folio-big { font-size: 22px; }
    .td-header-title { font-size: 19px; }
    .td-msg .body-wrap { max-width: 92%; }

    /* Acciones (estado, asignación, escalamiento…) arriba de la conversación,
       colapsadas detrás de un botón para no empujar el hilo hacia abajo. */
    .td-sidebar { position: static; order: -1; gap: 10px; }
    .td-sidebar-toggle {
        display: flex; align-items: center; justify-content: space-between; gap: 10px;
        width: 100%; padding: 11px 14px; border-radius: var(--td-radius-sm, 10px);
        border: 1px solid var(--c-border); background: var(--c-card); color: var(--c-text-1);
        font-size: 13.5px; font-weight: 700; font-family: inherit; cursor: pointer;
        box-shadow: 0 1px 2px rgba(15,23,42,.04);
    }
    .td-sidebar-toggle small { font-weight: 500; color: var(--c-text-3); }
    .td-sidebar:not(.open) > .td-card { display: none; }

    /* Cajón de tickets abiertos: pestaña chica abajo a la derecha que no tapa
       el compositor, panel casi a todo lo ancho con fondo para cerrar tocando fuera. */
    .td-drawer-tab {
        top: auto; bottom: 18px; right: 12px !important; transform: none;
        writing-mode: horizontal-tb; border-radius: 999px; padding: 10px 14px;
        font-size: 12px; letter-spacing: .4px; z-index: 1041;
    }
    .td-drawer-tab.open { display: none; }
    .td-drawer { width: min(340px, 88vw); z-index: 1046; }
    .td-drawer-backdrop.show {
        display: block; position: fixed; inset: 0; z-index: 1045;
        background: rgba(8, 29, 73, .45);
    }
    .td-drawer-close { padding: 8px; }
    .td-collab-row .remove, .td-cc-item .remove { padding: 8px; }
    /* 16px evita el zoom de iOS al enfocar */
    .td-select, .td-input, .td-canned-search, .td-composer textarea { font-size: 16px; }
}

/* Fade-in animation */
@keyframes td-fade {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: none; }
}
.td-fade { animation: td-fade .3s ease-out both; }

/* Flash-bump animation for legacy class compatibility */
.flash-bump { animation: td-flash-bump 1.4s ease-out; }
@keyframes td-flash-bump {
    0%   { background: rgba(245, 197, 24, 0); }
    15%  { background: rgba(245, 197, 24, 0.18); }
    100% { background: rgba(245, 197, 24, 0); }
}
`;


// ══════════════════════════════════════════════════════════════════════════════
// Componente principal
// ══════════════════════════════════════════════════════════════════════════════
export default function TicketDetail() {
    const { id } = useParams();
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const textareaRef = useRef(null);

    const [ticket,     setTicket]     = useState(null);
    const [status,     setStatus]     = useState('open');
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft,   setTitleDraft]   = useState('');
    const [assignedTo,  setAssignedTo]  = useState('');
    // eslint-disable-next-line no-unused-vars
    const [assignedTo2, setAssignedTo2] = useState('');
    const [collabToAdd, setCollabToAdd] = useState('');
    const [savingCollabs, setSavingCollabs] = useState(false);
    const [agents,     setAgents]     = useState([]);

    const [requiresAuthorization, setRequiresAuthorization] = useState(false);
    const [authorizerId,  setAuthorizerId]  = useState('');
    const [authorizer2Id, setAuthorizer2Id] = useState('');
    const [authorizer3Id, setAuthorizer3Id] = useState('');
    const [materialsCount,   setMaterialsCount]   = useState('');
    const [deliveryDateEdit, setDeliveryDateEdit] = useState('');
    const [authorizers,   setAuthorizers]   = useState([]);
    const [loadingAuth,   setLoadingAuth]   = useState(false);

    const [ccEmails,     setCcEmails]     = useState([]);
    const [ccInput,      setCcInput]      = useState('');
    const [loadingCc,    setLoadingCc]    = useState(false);

    const [profileUserId, setProfileUserId] = useState(null);

    const [comments,           setComments]           = useState([]);
    const [text,               setText]               = useState('');
    const [commentAttachments, setCommentAttachments] = useState([]);
    const [loadingTicket,      setLoadingTicket]      = useState(true);
    const [loadingStatus,      setLoadingStatus]      = useState(false);
    const [loadingAssign,      setLoadingAssign]      = useState(false);
    const [loadingComment,     setLoadingComment]     = useState(false);

    const [deleteCommentId,    setDeleteCommentId]    = useState(null);
    const [deleteReason,       setDeleteReason]       = useState('');
    const [loadingDelete,      setLoadingDelete]      = useState(false);

    const [activity,      setActivity]      = useState([]);
    const [activityOpen,  setActivityOpen]  = useState(false);

    const [showDrawer,    setShowDrawer]    = useState(false);
    const [sideOpen,      setSideOpen]      = useState(false); // panel de acciones en móvil
    const [drawerTickets, setDrawerTickets] = useState([]);
    const [loadingDrawer, setLoadingDrawer] = useState(false);

    const [cannedList,   setCannedList]   = useState([]);
    const [showCanned,   setShowCanned]   = useState(false);
    const [cannedSearch, setCannedSearch] = useState('');

    const [csatRating,    setCsatRating]    = useState(0);
    const [csatComment,   setCsatComment]   = useState('');
    const [csatPreset,    setCsatPreset]    = useState('');
    const [loadingCsat,   setLoadingCsat]   = useState(false);
    const [showCsatModal, setShowCsatModal] = useState(false);

    const [isInternal, setIsInternal] = useState(false);

    // ── Atajos del detalle (ver components/ShortcutsHelp.jsx) ──────────────
    // Va antes de los early-returns para respetar el orden de hooks; por eso
    // calcula su propio esStaff en vez de usar el isStaff de más abajo.
    const esStaffHK = user?.role === 'admin' || user?.role === 'agent' || user?.role === 'leader';
    const sinModalAbierto = () => !document.querySelector('.modal.show');
    const asignadoAMi = () => String(ticket?.assignedTo?._id || ticket?.assignedTo || '') === String(user?.id || '');
    useHotkeys([
        { keys: 'r', run: () => textareaRef.current?.focus(), when: sinModalAbierto },
        { keys: 'i', run: () => setIsInternal(v => !v),
          when: () => esStaffHK && ticket?.status !== 'closed' && sinModalAbierto() },
        { keys: 'a', run: () => handleAssign(user.id, 1),
          when: () => esStaffHK && !!ticket && ticket.status !== 'closed' && !asignadoAMi() && sinModalAbierto() },
        { keys: 'escape', run: () => navigate('/tickets'), when: sinModalAbierto },
    ]);

    const [loadingEscalate, setLoadingEscalate] = useState(false);

    const [departments,    setDepartments]    = useState([]);
    const [moveDeptId,     setMoveDeptId]     = useState('');
    const [loadingMoveDept, setLoadingMoveDept] = useState(false);

    // Edición de materialItems (solo admin / líder del depto)
    const [editingMaterials, setEditingMaterials] = useState(false);
    const [materialDraft,    setMaterialDraft]    = useState([]);
    const [savingMaterials,  setSavingMaterials]  = useState(false);

    useEffect(() => {
        setLoadingDrawer(true);
        api.get('/tickets', { params: { status: 'open', limit: 60 } })
            .then(r => {
                const arr = Array.isArray(r.data) ? r.data : (r.data.items || []);
                setDrawerTickets(arr);
            })
            .catch(() => {})
            .finally(() => setLoadingDrawer(false));
    }, []);

    const fetchAuthorizers = async (deptId) => {
        try {
            const res = await api.get('/users/authorizers', deptId ? { params: { department: deptId } } : {});
            setAuthorizers(res.data || []);
        } catch { /* silencioso */ }
    };

    const syncTicketState = (t, currentUid) => {
        setTicket(t);
        setStatus(t.status);
        setAssignedTo(t.assignedTo?._id   || '');
        setAssignedTo2(t.assignedTo2?._id || '');
        setRequiresAuthorization(t.requiresAuthorization || false);
        setAuthorizerId(t.authorizer?._id  || t.authorizer  || '');
        setAuthorizer2Id(t.authorizer2?._id || t.authorizer2 || '');
        setAuthorizer3Id(t.authorizer3?._id || t.authorizer3 || '');
        setMaterialsCount(t.materialsCount != null ? String(t.materialsCount) : '');
        setDeliveryDateEdit(t.deliveryDate ? new Date(t.deliveryDate).toISOString().slice(0, 10) : '');
        setCcEmails(t.ccEmails || []);

        const uid       = currentUid || String(user?.id || '');
        const isCreator = String(t.createdBy?._id || t.createdBy) === uid;
        const dismissed = sessionStorage.getItem(`csat_dismissed_${t._id}`);
        if (isCreator && t.status === 'closed' && !t.csatRating && !dismissed) {
            setTimeout(() => setShowCsatModal(true), 600);
        }
    };

    useEffect(() => {
        let alive = true;
        async function loadAll() {
            try {
                setLoadingTicket(true);
                const [tRes, cRes, aRes, dRes] = await Promise.all([
                    api.get(`/tickets/${id}`),
                    api.get(`/tickets/${id}/comments`),
                    api.get('/users', { params: { roles: 'agent,leader' } }),
                    api.get('/departments'),
                ]);
                if (!alive) return;
                syncTicketState(tRes.data);
                setComments(cRes.data);
                const deptId = String(tRes.data.department?._id || tRes.data.department || '');
                const allAgents = aRes.data || [];
                const allDepts  = dRes.data || [];

                let allowedDepts;
                if (deptId) {
                    const ticketDept = allDepts.find(d => String(d._id) === deptId);
                    const parentId   = String(ticketDept?.parent?._id || ticketDept?.parent || '');
                    const childIds   = allDepts
                        .filter(d => String(d.parent?._id || d.parent || '') === deptId)
                        .map(d => String(d._id));
                    allowedDepts = new Set([deptId, ...(parentId ? [parentId] : []), ...childIds]);
                }

                setAgents(deptId
                    ? allAgents.filter(a => (a.departments || []).some(d => allowedDepts.has(String(d._id || d))))
                    : allAgents
                );
                setDepartments(allDepts);

                const isCreator = String(tRes.data.createdBy?._id || tRes.data.createdBy) === String(user?.id);
                if (user?.role === 'agent' && !tRes.data.assignedTo && !isCreator) {
                    try {
                        const autoRes = await api.patch(`/tickets/${id}/auto-assign`);
                        if (alive) syncTicketState(autoRes.data);
                    } catch (autoErr) {
                        if (autoErr.response?.status === 409) {
                            const takenBy = autoErr.response?.data?.takenBy || 'otro agente';
                            toast.warn(`⚠️ Colisión — ${takenBy} tomó este ticket al mismo tiempo`, { autoClose: 6000 });
                            try {
                                const fresh = await api.get(`/tickets/${id}`);
                                if (alive) syncTicketState(fresh.data);
                            } catch { /* ignore */ }
                        }
                    }
                }

                if (user?.role === 'admin' || user?.role === 'agent' || user?.role === 'leader') {
                    const deptId = tRes.data.department?._id || tRes.data.department || '';
                    if (alive) await fetchAuthorizers(deptId);
                }
                if (user?.role === 'admin' || user?.role === 'agent' || user?.role === 'leader') {
                    const cannedRes = await api.get('/canned');
                    if (!alive) return;
                    setCannedList(cannedRes.data || []);
                }
            } catch (err) {
                console.error(err);
                toast.error('No se pudo cargar el ticket');
            } finally {
                if (alive) setLoadingTicket(false);
            }
        }
        loadAll();
        const joinRoom = () => socket.emit('joinTicket', id);
        joinRoom();
        socket.on('connect', joinRoom);

        const onCommentAdded = (newComment) => {
            const targetId = String(newComment.ticket?._id || newComment.ticket);
            if (targetId !== String(id)) return;
            const myRole = user?.role;
            const amStaff = ['admin', 'agent', 'leader'].includes(myRole);
            if (newComment.isInternal && !amStaff) return;
            setComments((prev) => {
                if (prev.some((c) => c._id === newComment._id)) return prev;
                return [newComment, ...prev];
            });
            const authorId = String(newComment.author?._id || newComment.author);
            if (authorId !== String(user?.id)) {
                playCommentSound();
                const label = newComment.isInternal ? 'Nota interna de' : 'Nuevo comentario de';
                toast.info(`${label} ${nombreVisible(newComment.author?.name, user?.role, newComment.author?.role) || 'alguien'}`);
            }
        };
        const loadActivity = () => {
            api.get(`/tickets/${id}/activity`)
                .then(r => { if (alive) setActivity(r.data || []); })
                .catch(() => {});
        };
        loadActivity();

        const onUpdated = (updated) => {
            if (String(updated._id) === String(id)) {
                syncTicketState(updated);
                loadActivity();
            }
        };
        const onCommentDeleted = ({ commentId, ticketId }) => {
            if (String(ticketId) !== String(id)) return;
            setComments(prev => prev.filter(c => String(c._id) !== String(commentId)));
            loadActivity();
        };
        socket.on('commentAdded',   onCommentAdded);
        socket.on('commentDeleted', onCommentDeleted);
        socket.on('ticketUpdated',  onUpdated);
        return () => {
            alive = false;
            socket.off('connect',        joinRoom);
            socket.off('commentAdded',   onCommentAdded);
            socket.off('commentDeleted', onCommentDeleted);
            socket.off('ticketUpdated',  onUpdated);
            socket.emit('leaveTicket', id);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleSaveTitle = async () => {
        const trimmed = titleDraft.trim();
        if (!trimmed || trimmed === ticket.title) { setEditingTitle(false); return; }
        try {
            const res = await api.patch(`/tickets/${id}`, { title: trimmed });
            setTicket(prev => ({ ...prev, title: res.data.title }));
            toast.success('Título actualizado');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al actualizar título');
        } finally {
            setEditingTitle(false);
        }
    };

    const handleSaveCc = async () => {
        setLoadingCc(true);
        try {
            const res = await api.patch(`/tickets/${id}/cc`, { ccEmails });
            setCcEmails(res.data.ccEmails);
            setCcInput('');
            toast.success('Correos CC guardados');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al guardar correos CC');
        } finally { setLoadingCc(false); }
    };

    const handleStatusChange = async () => {
        const prevStatus = ticket.status;
        const ticketId   = id;
        setLoadingStatus(true);
        try {
            const res = await api.patch(`/tickets/${id}`, { status });
            syncTicketState(res.data);
            const newLabel = STATUS_CFG[res.data.status]?.label || res.data.status;
            if (res.data.status === 'closed') {
                toast.success(
                    <UndoToast
                        message="Ticket cerrado"
                        onUndo={async () => {
                            try {
                                await api.patch(`/tickets/${ticketId}`, { status: prevStatus });
                                toast.info(`Restaurado a "${STATUS_CFG[prevStatus]?.label || prevStatus}"`);
                            } catch {
                                toast.error('No se pudo deshacer');
                            }
                        }}
                    />,
                    { autoClose: 5000, closeOnClick: false }
                );
                navigate('/tickets');
            } else {
                toast.success(`Estado actualizado a "${newLabel}"`);
            }
        } catch { toast.error('Error al actualizar estado'); }
        finally { setLoadingStatus(false); }
    };

    const handleSaveCollaborators = async (newList) => {
        setSavingCollabs(true);
        try {
            const res = await api.patch(`/tickets/${id}/collaborators`, {
                collaborators: newList.map(u => String(u?._id || u)),
            });
            syncTicketState(res.data);
            setCollabToAdd('');
            toast.success('Colaboradores actualizados');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al actualizar colaboradores');
        } finally { setSavingCollabs(false); }
    };

    const handleAssign = async (agentId, slot = 1) => {
        if (!agentId) return;
        const prevAssigneeId =
            slot === 2
                ? (ticket.assignedTo2?._id || ticket.assignedTo2 || null)
                : (ticket.assignedTo?._id  || ticket.assignedTo  || null);
        const prevAssigneeName =
            slot === 2 ? ticket.assignedTo2?.name : ticket.assignedTo?.name;
        const ticketId = id;
        setLoadingAssign(true);
        try {
            const res = await api.patch(`/tickets/${id}/assign`, { userId: agentId, slot });
            syncTicketState(res.data);
            const name = slot === 2 ? res.data.assignedTo2?.name : res.data.assignedTo?.name;
            if (prevAssigneeId && String(prevAssigneeId) !== String(agentId)) {
                toast.success(
                    <UndoToast
                        message={`Reasignado a ${name || 'agente'}`}
                        onUndo={async () => {
                            try {
                                const r = await api.patch(`/tickets/${ticketId}/assign`, { userId: prevAssigneeId, slot });
                                syncTicketState(r.data);
                                toast.info(`Restaurado a ${prevAssigneeName || 'agente anterior'}`);
                            } catch {
                                toast.error('No se pudo deshacer');
                            }
                        }}
                    />,
                    { autoClose: 5000, closeOnClick: false }
                );
            } else {
                toast.success(`Asignado a ${name || 'agente'}`);
            }
        } catch { toast.error('Error al asignar ticket'); }
        finally { setLoadingAssign(false); }
    };

    const handleSaveAuthorization = async () => {
        setLoadingAuth(true);
        try {
            const payload = { requiresAuthorization };
            if (requiresAuthorization) {
                if (authorizerId)  payload.authorizer  = authorizerId;
                if (authorizer2Id) payload.authorizer2 = authorizer2Id;
                if (authorizer3Id) payload.authorizer3 = authorizer3Id;
            }
            const res = await api.patch(`/tickets/${id}`, payload);
            syncTicketState(res.data);
            toast.success('Autorización guardada');
        } catch { toast.error('Error al guardar autorización'); }
        finally { setLoadingAuth(false); }
    };

    const handleAuthorize = async (decision) => {
        setLoadingAuth(true);
        try {
            const res = await api.patch(`/tickets/${id}/authorize`, { decision });
            syncTicketState(res.data);
            toast.success(decision === 'approved' ? 'Ticket aprobado' : 'Ticket rechazado');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al procesar autorización');
        } finally { setLoadingAuth(false); }
    };

    const handleSubmitComment = async (e) => {
        e.preventDefault();
        if (!text.trim() && commentAttachments.length === 0) {
            toast.warn('Escribe un mensaje o adjunta al menos una imagen');
            return;
        }
        setLoadingComment(true);
        try {
            const res = await api.post(`/tickets/${id}/comments`, { text, attachments: commentAttachments, isInternal });
            setComments((prev) => {
                if (prev.some((c) => c._id === res.data._id)) return prev;
                return [res.data, ...prev];
            });
            setText('');
            setCommentAttachments([]);
            setIsInternal(false);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al enviar comentario');
        } finally { setLoadingComment(false); }
    };

    const handleSubmitAndClose = async () => {
        if (!text.trim() && commentAttachments.length === 0) {
            toast.warn('Escribe un mensaje o adjunta al menos una imagen');
            return;
        }
        const prevStatus = ticket.status;
        const ticketId   = id;
        setLoadingComment(true);
        try {
            const res = await api.post(`/tickets/${id}/comments`, { text, attachments: commentAttachments, isInternal });
            setComments((prev) => {
                if (prev.some((c) => c._id === res.data._id)) return prev;
                return [res.data, ...prev];
            });
            setText('');
            setCommentAttachments([]);
            // takeOwnership: quien envía y cierra se queda con el ticket aunque lo tuviera otro agente
            await api.patch(`/tickets/${id}`, { status: 'closed', takeOwnership: true });
            toast.success(
                <UndoToast
                    message="Comentario enviado y ticket cerrado"
                    onUndo={async () => {
                        try {
                            await api.patch(`/tickets/${ticketId}`, { status: prevStatus });
                            toast.info('Ticket reabierto');
                        } catch {
                            toast.error('No se pudo deshacer');
                        }
                    }}
                />,
                { autoClose: 5000, closeOnClick: false }
            );
            navigate('/tickets');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al enviar comentario');
        } finally { setLoadingComment(false); }
    };

    const handleConfirmDeleteComment = async () => {
        const reason = deleteReason.trim();
        if (!deleteCommentId) return;
        if (!reason) { toast.warn('Captura el motivo del borrado'); return; }
        setLoadingDelete(true);
        try {
            await api.delete(`/tickets/${id}/comments/${deleteCommentId}`, { data: { reason } });
            setComments(prev => prev.filter(c => String(c._id) !== String(deleteCommentId)));
            toast.success('Comentario borrado');
            setDeleteCommentId(null);
            setDeleteReason('');
            api.get(`/tickets/${id}/activity`).then(r => setActivity(r.data || [])).catch(() => {});
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al borrar comentario');
        } finally { setLoadingDelete(false); }
    };

    const handleSubmitCsat = async (fromModal = false) => {
        if (!csatRating) { toast.warn('Selecciona una calificación'); return; }
        setLoadingCsat(true);
        try {
            const finalComment = csatComment || csatPreset;
            const res = await api.patch(`/tickets/${id}/csat`, { csatRating, csatComment: finalComment });
            syncTicketState(res.data);
            toast.success('¡Gracias por tu calificación!');
            if (fromModal) setShowCsatModal(false);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al enviar calificación');
        } finally { setLoadingCsat(false); }
    };

    const handleEscalate = async () => {
        if (!window.confirm('¿Escalar el ticket a la siguiente prioridad?')) return;
        setLoadingEscalate(true);
        try {
            const res = await api.patch(`/tickets/${id}/escalate`);
            syncTicketState(res.data);
            toast.success(`Prioridad escalada a "${PRIORITY_CFG[res.data.priority]?.label || res.data.priority}"`);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al escalar ticket');
        } finally { setLoadingEscalate(false); }
    };

    const startEditMaterials = () => {
        setMaterialDraft(materialItems.map(it => ({ type: it.type, count: Number(it.count) || 1 })));
        setEditingMaterials(true);
    };
    const updateMaterialRow = (i, patch) =>
        setMaterialDraft(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    const addMaterialRow = () =>
        setMaterialDraft(prev => [...prev, { type: '', count: 1 }]);
    const removeMaterialRow = (i) =>
        setMaterialDraft(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

    const handleSaveMaterials = async () => {
        const clean = materialDraft
            .filter(it => it.type && Number(it.count) >= 1)
            .map(it => ({ type: it.type, count: Number(it.count) }));
        if (clean.length === 0) { toast.warn('Agrega al menos un material'); return; }
        setSavingMaterials(true);
        try {
            const res = await api.patch(`/tickets/${id}`, { materialItems: clean });
            syncTicketState(res.data);
            toast.success('Materiales actualizados');
            setEditingMaterials(false);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al actualizar materiales');
        } finally { setSavingMaterials(false); }
    };

    const handleMoveDept = async () => {
        if (!moveDeptId) return;
        const target = departments.find(d => d._id === moveDeptId);
        if (!window.confirm(`¿Mover este ticket a "${target?.name}"?`)) return;
        setLoadingMoveDept(true);
        try {
            const res = await api.patch(`/tickets/${id}`, { department: moveDeptId });
            syncTicketState(res.data);
            setMoveDeptId('');
            const newDeptId = res.data.department?._id || res.data.department || '';
            fetchAuthorizers(newDeptId);
            toast.success(`Ticket movido a "${res.data.department?.name || target?.name}"`);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al mover el ticket');
        } finally { setLoadingMoveDept(false); }
    };

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loadingTicket || !ticket) {
        return (
            <>
                <style>{TD_CSS}</style>
                <div className="td-root">
                    <div className="td-breadcrumb">
                        <Skeleton variant="line" width={120} height={20} />
                    </div>
                    <div className="td-layout">
                        <div className="td-main">
                            <div className="td-card td-card-pad">
                                <Skeleton variant="line" width="70%" height={28} />
                                <div style={{ marginTop: 12 }}>
                                    <Skeleton variant="line" width="50%" height={14} />
                                </div>
                            </div>
                            <div className="td-card td-card-pad"><Skeleton variant="block" height={100} /></div>
                            <div className="td-card td-card-pad"><Skeleton variant="block" height={220} /></div>
                        </div>
                        <div className="td-sidebar">
                            <div className="td-card td-card-pad-sm"><Skeleton variant="block" height={140} /></div>
                            <div className="td-card td-card-pad-sm"><Skeleton variant="block" height={100} /></div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    const isStaff = user?.role === 'admin' || user?.role === 'agent' || user?.role === 'leader';
    const uid     = String(user?.id || '');
    const isMyAuth1 = user?.role === 'authorizer' &&
        String(ticket.authorizer?._id || ticket.authorizer || '') === uid;
    const isMyAuth2 = user?.role === 'authorizer' &&
        String(ticket.authorizer2?._id || ticket.authorizer2 || '') === uid;
    const isMyAuth3 = user?.role === 'authorizer' &&
        String(ticket.authorizer3?._id || ticket.authorizer3 || '') === uid;
    const isMyAuthorization = isMyAuth1 || isMyAuth2 || isMyAuth3;
    const auth1Status = resolveAuth1Status(ticket);
    const auth2Status = ticket.authorizer2Status || 'pending';
    const auth3Status = ticket.authorizer3Status || 'pending';
    const myStatus    = isMyAuth1 ? auth1Status : isMyAuth2 ? auth2Status : auth3Status;
    const myDecidedAt = isMyAuth1 ? ticket.authorizerDecidedAt : isMyAuth2 ? ticket.authorizer2DecidedAt : ticket.authorizer3DecidedAt;
    const canDecide   = isMyAuthorization && myStatus === 'pending' && ticket.authorizationStatus === 'pending';

    const isCreatorView = String(ticket.createdBy?._id || ticket.createdBy) === uid;
    const showMaterialsBlock = ticket.department?.slaMode === 'deliveryDate' || ticket.department?.slaMode === 'materialType';
    const materialItems = (ticket.materialItems && ticket.materialItems.length > 0)
        ? ticket.materialItems
        : (ticket.materialType ? [{ type: ticket.materialType, count: ticket.materialsCount || 1 }] : []);

    const filteredCanned = cannedList.filter(c =>
        !cannedSearch ||
        c.title.toLowerCase().includes(cannedSearch.toLowerCase()) ||
        c.body.toLowerCase().includes(cannedSearch.toLowerCase())
    );

    // Admin siempre, o líder del depto del ticket pueden editar materialItems
    const ticketDeptId = String(ticket.department?._id || ticket.department || '');
    const isDeptLeader = user?.role === 'leader' &&
        (user.departments || []).some(d => String(d._id || d) === ticketDeptId);
    const canEditMaterials = user?.role === 'admin' || isDeptLeader;

    // Catálogo de tipos activos del depto (para el dropdown)
    const activeMaterialTypes = (ticket.department?.materialTypes || []).filter(t => t.active !== false);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            <style>{TD_CSS}</style>
            <div className="td-root">

                {/* Breadcrumb */}
                <div className="td-breadcrumb">
                    <Link to="/tickets">{I.back} Tickets</Link>
                    <span style={{ color: 'var(--c-text-4)' }}>/</span>
                    <span className="td-folio">#{ticket.folio || '—'}</span>
                </div>

                <div className="td-layout">

                    {/* ═════════════════ MAIN ═════════════════ */}
                    <div className="td-main td-fade">

                        {/* Header */}
                        <div className="td-card td-card-pad">
                            <div className="td-pills-row">
                                <StatusPill status={ticket.status} />
                                <PriorityPill priority={ticket.priority} />
                                {ticket.isVip && (
                                    <span className="td-pill" style={{ background: '#fef3c7', color: '#92400e', fontWeight: 800 }}
                                          title={`Solicitante VIP${ticket.createdBy?.puesto ? `: ${ticket.createdBy.puesto}` : ''}`}>
                                        ★ VIP
                                    </span>
                                )}
                                {ticket.isCancelacion && (
                                    <span className="td-pill" style={{ background: '#fee2e2', color: '#b91c1c' }}
                                          title={ticket.cancelacionPendiente ? 'Cancelación pendiente de resolver' : 'Cancelación (resuelta)'}>
                                        ⚠ Cancelación
                                    </span>
                                )}
                                <span className="td-pill td-pill-neutral">
                                    <span style={{ opacity: 0.7, display: 'inline-flex' }}>{I.building}</span>
                                    {ticket.department?.name || '—'}
                                </span>
                                {ticket.pais && (
                                    <span className="td-pill td-pill-country">🌎 {ticket.pais}</span>
                                )}
                            </div>

                            {isStaff && editingTitle ? (
                                <div className="td-title-edit">
                                    <input
                                        autoFocus
                                        className="td-title-input"
                                        value={titleDraft}
                                        onChange={e => setTitleDraft(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') handleSaveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                                    />
                                    <button type="button" className="td-btn td-btn-primary" onClick={handleSaveTitle}>Guardar</button>
                                    <button type="button" className="td-btn td-btn-ghost" onClick={() => setEditingTitle(false)}>{I.x}</button>
                                </div>
                            ) : (
                                <h1
                                    className={`td-header-title${isStaff ? ' editable' : ''}`}
                                    title={isStaff ? 'Clic para editar título' : undefined}
                                    onClick={() => { if (isStaff) { setTitleDraft(ticket.title); setEditingTitle(true); } }}
                                >
                                    <Copyable
                                        className="td-header-folio-big"
                                        value={ticket.folio}
                                        mensaje={`Folio #${ticket.folio} copiado`}
                                        title="Copiar folio para compartir"
                                    >#{ticket.folio || '—'}</Copyable>
                                    {ticket.title}
                                    {isStaff && (
                                        <span style={{ marginLeft: 10, display: 'inline-flex', verticalAlign: 'middle', color: 'var(--c-text-4)' }}>{I.pencil}</span>
                                    )}
                                </h1>
                            )}

                            <div className="td-meta-row">
                                <span>
                                    <Avatar name={ticket.createdBy?.name} size={20} />
                                    <strong>
                                        <UserNameButton
                                            userId={ticket.createdBy?._id}
                                            name={ticket.createdBy?.name || '—'}
                                            onClick={setProfileUserId}
                                        />
                                    </strong>
                                    {isStaff && ticket.createdBy?.idEmpleado && (
                                        <Copyable className="td-tag td-tag-strong"
                                                  value={ticket.createdBy.idEmpleado}
                                                  mensaje={`ID ${ticket.createdBy.idEmpleado} copiado`}
                                                  title="Copiar ID del solicitante">
                                            ID {ticket.createdBy.idEmpleado}
                                        </Copyable>
                                    )}
                                    {isStaff && ticket.createdBy?.puesto && (
                                        <span className="td-tag td-tag-strong">{ticket.createdBy.puesto}</span>
                                    )}
                                    {isStaff && ticket.createdBy?.unidad && (
                                        <span className="td-tag">{ticket.createdBy.unidad}</span>
                                    )}
                                    {isStaff && ticket.createdBy?.sucursal && (
                                        <span className="td-tag">{ticket.createdBy.sucursal}</span>
                                    )}
                                </span>
                                <span>
                                    {I.clock}
                                    <strong>{new Date(ticket.createdAt).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })}</strong>
                                </span>
                                {ticket.dueAt && ticket.status !== 'closed' && (
                                    <span><SLAChip dueAt={ticket.dueAt} /></span>
                                )}
                                {isStaff && ticket.assignedTo?.name && (
                                    <span>
                                        <span className="td-meta-label">Agente</span>
                                        <strong>{ticket.assignedTo.name}</strong>
                                    </span>
                                )}
                                {isStaff && ticket.assignedTo2?.name && (
                                    <span>
                                        <span className="td-meta-label">Agente 2</span>
                                        <strong>{ticket.assignedTo2.name}</strong>
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Descripción */}
                        <div className="td-card td-card-pad">
                            <p className="td-label">Descripción</p>
                            <p className="td-desc">{ticket.description}</p>
                            {ticket.attachments?.length > 0 && (
                                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--c-card-header-border)' }}>
                                    <AttachmentGrid urls={ticket.attachments} />
                                </div>
                            )}
                        </div>

                        {/* Materiales (1 número) */}
                        {ticket.department?.slaMode === 'deliveryDate' && ticket.materialsCount != null && (
                            <div className="td-card td-card-pad">
                                <p className="td-label">Materiales solicitados</p>
                                <div className="td-material-single">
                                    <span style={{ display: 'inline-flex', color: 'var(--td-accent)' }}>{I.package}</span>
                                    <span className="num">{ticket.materialsCount}</span>
                                </div>
                            </div>
                        )}

                        {/* Materiales por tipo */}
                        {ticket.department?.slaMode === 'materialType' && (materialItems.length > 0 || editingMaterials) && (() => {
                            const typeMap = new Map(
                                (ticket.department?.materialTypes || []).map(t => [String(t.name), Number(t.slaHours) || 0])
                            );
                            const totalHours = materialItems.reduce(
                                (acc, it) => acc + (typeMap.get(it.type) || 0) * (Number(it.count) || 0), 0
                            );
                            const draftTotal = materialDraft.reduce(
                                (acc, it) => acc + (typeMap.get(it.type) || 0) * (Number(it.count) || 0), 0
                            );
                            return (
                                <div className="td-card td-card-pad">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <p className="td-label" style={{ margin: 0 }}>Materiales solicitados</p>
                                        {canEditMaterials && !editingMaterials && (
                                            <button
                                                type="button"
                                                className="td-btn td-btn-ghost"
                                                onClick={startEditMaterials}
                                                style={{ padding: '4px 10px', fontSize: 12 }}
                                            >
                                                {I.pencil} Editar
                                            </button>
                                        )}
                                    </div>

                                    {!editingMaterials ? (
                                        <>
                                            <div className="td-materials-grid">
                                                {materialItems.map((it, i) => {
                                                    const perUnit = typeMap.get(it.type) || 0;
                                                    return (
                                                        <div key={i} className="td-material">
                                                            <p className="td-material-name">{it.type}</p>
                                                            <p className="td-material-meta">
                                                                ×{it.count}{isStaff && perUnit > 0 && ` · ${perUnit} h/u`}
                                                            </p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {isStaff && totalHours > 0 && (
                                                <div className="td-materials-total">
                                                    <span style={{ color: 'var(--c-text-3)' }}>Estimado interno (Σ horas × cantidad)</span>
                                                    <strong>{totalHours} h</strong>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {materialDraft.map((it, i) => (
                                                <div key={i} style={{
                                                    display: 'flex', gap: 8, alignItems: 'center',
                                                    border: '1px solid var(--c-border)', borderRadius: 10,
                                                    padding: '8px 10px', background: 'var(--c-input-bg)',
                                                }}>
                                                    <select
                                                        className="td-select"
                                                        style={{ flex: '2 1 0', maxWidth: 320 }}
                                                        value={it.type}
                                                        onChange={e => updateMaterialRow(i, { type: e.target.value })}
                                                    >
                                                        <option value="">— Tipo —</option>
                                                        {activeMaterialTypes.map((t, j) => (
                                                            <option key={j} value={t.name}>
                                                                {t.name} ({t.slaHours} h/u)
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <input
                                                        type="number" min={1}
                                                        className="td-input"
                                                        style={{ flex: '0 0 96px' }}
                                                        value={it.count}
                                                        onChange={e => updateMaterialRow(i, { count: Number(e.target.value) })}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="td-btn td-btn-ghost"
                                                        style={{ padding: '6px 10px' }}
                                                        onClick={() => removeMaterialRow(i)}
                                                        disabled={materialDraft.length === 1}
                                                        title="Quitar"
                                                    >{I.x}</button>
                                                </div>
                                            ))}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
                                                <button
                                                    type="button"
                                                    className="td-btn td-btn-soft"
                                                    style={{ padding: '6px 12px', fontSize: 12 }}
                                                    onClick={addMaterialRow}
                                                >
                                                    {I.plus} Agregar material
                                                </button>
                                                {draftTotal > 0 && (
                                                    <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>
                                                        Estimado: <strong style={{ color: 'var(--c-text-1)' }}>{draftTotal} h</strong>
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: 8, marginTop: 8, paddingTop: 10, borderTop: '1px solid var(--c-card-header-border)' }}>
                                                <button
                                                    type="button"
                                                    className="td-btn td-btn-primary"
                                                    onClick={handleSaveMaterials}
                                                    disabled={savingMaterials}
                                                >
                                                    {savingMaterials ? <Spinner size="sm" animation="border" /> : <>{I.check} Guardar cambios</>}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="td-btn td-btn-ghost"
                                                    onClick={() => setEditingMaterials(false)}
                                                    disabled={savingMaterials}
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Fecha de entrega (visible al solicitante) */}
                        {!isStaff && showMaterialsBlock && ticket.deliveryDate && (
                            <div className="td-card td-card-pad">
                                <p className="td-label">Fecha de entrega requerida</p>
                                <DeliveryCalendar deliveryDate={ticket.deliveryDate} />
                            </div>
                        )}

                        {/* CSAT — solicitante con ticket cerrado sin calificar */}
                        {isCreatorView && ticket.status === 'closed' && !ticket.csatRating && (
                            <div className="td-card td-card-pad td-csat-card">
                                <p className="td-label" style={{ color: '#b45309' }}>¿Cómo calificarías la atención recibida?</p>
                                <StarRating value={csatRating} onChange={setCsatRating} />
                                <textarea
                                    rows={2}
                                    className="td-input"
                                    style={{ marginTop: 12, marginBottom: 12, resize: 'vertical' }}
                                    value={csatComment}
                                    onChange={e => setCsatComment(e.target.value)}
                                    placeholder="Comentario opcional…"
                                />
                                <button
                                    type="button" className="td-btn td-btn-primary"
                                    onClick={() => handleSubmitCsat(false)} disabled={loadingCsat || !csatRating}
                                >
                                    {loadingCsat ? <Spinner size="sm" animation="border" /> : <>{I.star} Enviar calificación</>}
                                </button>
                            </div>
                        )}

                        {/* CSAT — ya calificado por el solicitante */}
                        {isCreatorView && ticket.csatRating != null && (
                            <div className="td-card td-card-pad">
                                <p className="td-label">Calificación del servicio</p>
                                <StarRating value={ticket.csatRating} readOnly size={24} />
                                {ticket.csatComment && (
                                    <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--c-text-2)', fontStyle: 'italic' }}>
                                        "{ticket.csatComment}"
                                    </p>
                                )}
                                {ticket.csatAt && (
                                    <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--c-text-4)' }}>
                                        Calificado el {fmt(ticket.csatAt)}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* CSAT read-only para staff */}
                        {isStaff && ticket.csatRating != null && (
                            <div className="td-card td-card-pad td-csat-card">
                                <p className="td-label" style={{ color: '#b45309' }}>Calificación CSAT</p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <StarRating value={ticket.csatRating} readOnly size={22} />
                                    <span style={{ fontSize: 13, color: 'var(--c-text-3)' }}>
                                        {ticket.csatComment || '—'}
                                    </span>
                                </div>
                                {ticket.csatAt && (
                                    <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--c-text-4)' }}>
                                        {fmt(ticket.csatAt)}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Conversación */}
                        <div className="td-card td-card-pad">
                            <p className="td-label">Conversación · {comments.length} mensaje{comments.length !== 1 ? 's' : ''}</p>

                            {/* Lista de comentarios */}
                            {comments.length === 0 ? (
                                <p className="td-empty-conv">Sin comentarios aún. Sé el primero en responder.</p>
                            ) : (
                                <div className="td-thread">
                                    {comments.map(c => {
                                        const authorId = String(c.author?._id || c.author);
                                        const mine = authorId === uid;
                                        // Al solicitante (no staff) se le muestra el nombre del
                                        // agente acortado; entre staff, completo.
                                        const autorEsStaff = c.author?.role && c.author.role !== 'user';
                                        const nombreAutor = (!isStaff && autorEsStaff)
                                            ? nombreCorto(c.author?.name)
                                            : (c.author?.name || '—');
                                        return (
                                            <div key={c._id} className={`td-msg${mine ? ' mine' : ''}${c.isInternal ? ' internal' : ''}`}>
                                                <Avatar name={nombreAutor} size={34} />
                                                <div className="body-wrap">
                                                    <div className="author-row">
                                                        <span className="author">{nombreAutor}</span>
                                                        {c.author?.role && c.author.role !== 'user' && (
                                                            <span className={`td-role-badge td-role-${c.author.role}`}>
                                                                {c.author.role === 'admin' ? 'Admin' : c.author.role === 'agent' ? 'Agente' : c.author.role === 'leader' ? 'Líder' : c.author.role}
                                                            </span>
                                                        )}
                                                        <span className="time" title={absoluteTime(c.createdAt)}>{fmt(c.createdAt)}</span>
                                                        {c.isInternal && (
                                                            <span className="td-internal-badge">{I.lock} Nota interna</span>
                                                        )}
                                                        {user?.role === 'admin' && (
                                                            <button
                                                                type="button" className="td-comment-delete"
                                                                onClick={() => { setDeleteCommentId(c._id); setDeleteReason(''); }}
                                                                title="Borrar comentario"
                                                            >
                                                                {I.trash} Borrar
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="bubble">
                                                        {c.text && <p>{c.text}</p>}
                                                        {c.attachments?.length > 0 && (
                                                            <div style={{ marginTop: c.text ? 10 : 0 }}>
                                                                <AttachmentGrid urls={c.attachments} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Composer */}
                            <Form onSubmit={handleSubmitComment} style={{ marginTop: 22 }}>
                                <div className={`td-composer${isInternal ? ' internal' : ''}`}>
                                    <textarea
                                        ref={textareaRef}
                                        value={text}
                                        onChange={e => setText(e.target.value)}
                                        onKeyDown={e => {
                                            // Esc: salir del cuadro (y volver a tener los atajos de una tecla).
                                            if (e.key === 'Escape') { e.currentTarget.blur(); return; }
                                            // Ctrl+Enter envía; Ctrl+Shift+Enter envía y cierra (solo staff).
                                            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                                                e.preventDefault();
                                                if (loadingComment) return;
                                                if (e.shiftKey) {
                                                    if (esStaffHK && ticket?.status !== 'closed') handleSubmitAndClose();
                                                } else {
                                                    handleSubmitComment(e);
                                                }
                                            }
                                        }}
                                        placeholder={isInternal ? 'Escribe una nota interna (solo la ve el equipo)… Ctrl+Enter envía' : 'Escribe tu respuesta… Ctrl+Enter envía'}
                                        rows={3}
                                    />

                                    {/* Adjuntos (sección propia para no competir con la toolbar) */}
                                    <div className="td-composer-uploader">
                                        <ImageUploader
                                            urls={commentAttachments}
                                            onChange={setCommentAttachments}
                                            maxFiles={20}
                                            departmentId={ticket.department?._id || ''}
                                            allowExtendedUploads={!!ticket.department?.allowExtendedUploads}
                                            globalDrop
                                        />
                                    </div>

                                    {/* Toolbar de acciones */}
                                    <div className="td-composer-bar">
                                        <div className="td-composer-bar-left">
                                            {isStaff && (
                                                <button
                                                    type="button"
                                                    className="td-composer-icon-btn"
                                                    onClick={() => { setShowCanned(v => !v); setCannedSearch(''); }}
                                                >
                                                    {I.zap} Respuestas rápidas
                                                </button>
                                            )}
                                            {isStaff && (
                                                <button
                                                    type="button"
                                                    className={`td-internal-toggle${isInternal ? ' on' : ''}`}
                                                    onClick={() => setIsInternal(v => !v)}
                                                >
                                                    {I.lock} Nota interna
                                                </button>
                                            )}
                                        </div>
                                        <div className="td-composer-bar-right">
                                            {isStaff && ticket.status !== 'closed' ? (
                                                <Dropdown as={ButtonGroup}>
                                                    <Button
                                                        type="submit" variant="primary"
                                                        disabled={loadingComment}
                                                        style={{ borderRadius: '8px 0 0 8px', paddingInline: 18, fontFamily: 'inherit', fontWeight: 600, fontSize: 13 }}
                                                    >
                                                        {loadingComment ? <Spinner size="sm" animation="border" /> : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{I.send} Enviar</span>}
                                                    </Button>
                                                    <Dropdown.Toggle
                                                        split variant="primary"
                                                        disabled={loadingComment}
                                                        style={{ borderRadius: '0 8px 8px 0' }}
                                                    />
                                                    <Dropdown.Menu align="end">
                                                        <Dropdown.Item onClick={handleSubmitAndClose}>
                                                            Enviar y cerrar ticket
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            ) : (
                                                <button
                                                    type="submit" className="td-btn td-btn-primary"
                                                    disabled={loadingComment}
                                                >
                                                    {loadingComment ? <Spinner size="sm" animation="border" /> : <>{I.send} Enviar</>}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Dropdown de respuestas rápidas (abre HACIA ARRIBA desde la toolbar) */}
                                    {showCanned && (
                                        <div className="td-canned-dropdown">
                                            {cannedList.length === 0 ? (
                                                <p style={{ padding: '16px 14px', fontSize: 13, color: 'var(--c-text-4)', margin: 0 }}>
                                                    No tienes respuestas rápidas.<br />
                                                    <span style={{ fontSize: 12 }}>Créalas en Ajustes → Respuestas Rápidas.</span>
                                                </p>
                                            ) : (
                                                <>
                                                    <input
                                                        autoFocus
                                                        type="text"
                                                        placeholder="Buscar…"
                                                        value={cannedSearch}
                                                        onChange={e => setCannedSearch(e.target.value)}
                                                        className="td-canned-search"
                                                    />
                                                    {filteredCanned.map(c => (
                                                        <div
                                                            key={c._id}
                                                            className="td-canned-item"
                                                            onClick={() => {
                                                                setText(prev => prev ? `${prev}\n${c.body}` : c.body);
                                                                setShowCanned(false);
                                                            }}
                                                        >
                                                            <p className="title">{c.title}</p>
                                                            <p className="body">{c.body}</p>
                                                        </div>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Form>
                        </div>

                        {/* Historial de actividad */}
                        {activity.length > 0 && (
                            <div className="td-card" style={{ overflow: 'hidden' }}>
                                <button
                                    type="button" className="td-activity-toggle"
                                    onClick={() => setActivityOpen(v => !v)}
                                    style={{ borderBottom: activityOpen ? '1px solid var(--c-border)' : 'none' }}
                                >
                                    <span className="label">
                                        Historial de actividad
                                        <span className="count">({activity.length})</span>
                                    </span>
                                    <span className="indicator">
                                        {activityOpen ? <>Ocultar {I.chevronUp}</> : <>Mostrar {I.chevron}</>}
                                    </span>
                                </button>
                                {activityOpen && (
                                    <div className="td-activity-body">
                                        {activity.map((log, i) => (
                                            <div key={log._id} className="td-activity-item">
                                                {i < activity.length - 1 && <div className="line" />}
                                                <div className="dot" style={{ background: ACTION_BG[log.action] || '#f3f4f6', color: ACTION_COLOR[log.action] || 'var(--c-text-2)' }}>
                                                    {ACTION_ICON_NODE[log.action] || <span>•</span>}
                                                </div>
                                                <div className="body">
                                                    <p className="text">{formatActivity(log, user?.role)}</p>
                                                    <p className="meta" title={absoluteTime(log.createdAt)}>
                                                        {nombreVisible(log.user?.name, user?.role, log.user?.role)} · {fmt(log.createdAt)}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ═════════════════ SIDEBAR ═════════════════ */}
                    <div className={`td-sidebar td-fade${sideOpen ? ' open' : ''}`}>
                        {/* Solo móvil: el panel va arriba del hilo, colapsado */}
                        <button type="button" className="td-sidebar-toggle" onClick={() => setSideOpen(v => !v)} aria-expanded={sideOpen}>
                            <span>Acciones del ticket <small>· estado, asignación, más</small></span>
                            <span aria-hidden="true">{sideOpen ? '▲' : '▼'}</span>
                        </button>

                        {/* Estado */}
                        {isStaff && (
                            <div className="td-card td-card-pad-sm">
                                <p className="td-label">Estado</p>
                                <div style={{ marginBottom: 10 }}>
                                    <StatusPill status={ticket.status} />
                                </div>
                                <select
                                    className="td-select"
                                    value={status}
                                    onChange={e => setStatus(e.target.value)}
                                    style={{ marginBottom: 8 }}
                                >
                                    <option value="open">Abierto</option>
                                    <option value="in_progress">En progreso</option>
                                    <option value="closed">Cerrado</option>
                                </select>
                                <button
                                    type="button" className="td-btn td-btn-primary full"
                                    onClick={handleStatusChange} disabled={loadingStatus}
                                >
                                    {loadingStatus ? <Spinner size="sm" animation="border" /> : <>{I.check} Guardar</>}
                                </button>
                            </div>
                        )}

                        {/* Asignado */}
                        {isStaff && (
                            <div className="td-card td-card-pad-sm">
                                <p className="td-label">
                                    {ticket.assignedTo?.name ? 'Asignado a' : 'Sin asignar'}
                                </p>
                                {ticket.assignedTo?.name ? (
                                    <div className="td-assignee">
                                        <Avatar name={ticket.assignedTo.name} size={34} ring />
                                        <div>
                                            <div className="name">
                                                <UserNameButton
                                                    userId={ticket.assignedTo._id}
                                                    name={ticket.assignedTo.name}
                                                    onClick={setProfileUserId}
                                                />
                                            </div>
                                            {ticket.assignedTo.role && (
                                                <div className="role">{ticket.assignedTo.role === 'agent' ? 'Agente' : ticket.assignedTo.role === 'leader' ? 'Líder' : ticket.assignedTo.role === 'admin' ? 'Admin' : ticket.assignedTo.role}</div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p style={{ fontSize: 12, color: 'var(--c-text-4)', marginBottom: 12 }}>Aún no hay agente asignado</p>
                                )}

                                {/* Colaboradores stack */}
                                {(ticket.collaborators?.length > 0) && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                        <span style={{ fontSize: 10, color: 'var(--c-text-4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>+ colaboran</span>
                                        <div className="td-collab-stack">
                                            {ticket.collaborators.map((c, i) => (
                                                <div key={c?._id || i}><Avatar name={c?.name || '?'} size={24} ring /></div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <select
                                    className="td-select"
                                    value={assignedTo}
                                    onChange={e => setAssignedTo(e.target.value)}
                                    style={{ marginBottom: 8 }}
                                >
                                    <option value="">— Reasignar a agente —</option>
                                    {agents.map(a => (
                                        <option key={a._id} value={a._id}>{a.name}</option>
                                    ))}
                                </select>
                                <button
                                    type="button" className="td-btn td-btn-soft full"
                                    onClick={() => handleAssign(assignedTo, 1)}
                                    disabled={!assignedTo || loadingAssign}
                                >
                                    {loadingAssign ? <Spinner size="sm" animation="border" /> : (ticket.assignedTo?.name ? 'Reasignar' : 'Asignar')}
                                </button>
                            </div>
                        )}

                        {/* Co-asignar (admin/leader) */}
                        {(user?.role === 'admin' || user?.role === 'leader') && (() => {
                            const collabs = ticket.collaborators || [];
                            const principalId = String(ticket.assignedTo?._id || ticket.assignedTo || '');
                            const collabIds   = collabs.map(c => String(c?._id || c));
                            const availableAgents = agents.filter(a =>
                                String(a._id) !== principalId &&
                                !collabIds.includes(String(a._id))
                            );
                            return (
                                <div className="td-card td-card-pad-sm">
                                    <p className="td-label">Co-asignar a más agentes</p>
                                    <p style={{ fontSize: 11, color: 'var(--c-text-4)', margin: '0 0 10px' }}>
                                        Sumá agentes del depto que pueden responder, comentar y cambiar el estado, sin quitarle el ticket al principal.
                                    </p>
                                    {collabs.length === 0 ? (
                                        <p style={{ fontSize: 12, color: 'var(--c-text-4)', marginBottom: 8 }}>Sin agentes adicionales</p>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                                            {collabs.map(c => {
                                                const cid = String(c?._id || c);
                                                return (
                                                    <div key={cid} className="td-collab-row">
                                                        <Avatar name={c.name || '?'} size={24} />
                                                        <span style={{ fontSize: 13, color: 'var(--c-text-2)', flex: 1 }}>
                                                            <UserNameButton userId={cid} name={c.name || '—'} onClick={setProfileUserId} />
                                                        </span>
                                                        <button
                                                            type="button" className="remove"
                                                            onClick={() => handleSaveCollaborators(collabs.filter(x => String(x?._id || x) !== cid))}
                                                            disabled={savingCollabs}
                                                            title="Quitar"
                                                        >{I.x}</button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {availableAgents.length > 0 ? (
                                        <>
                                            <select
                                                className="td-select"
                                                value={collabToAdd}
                                                onChange={e => setCollabToAdd(e.target.value)}
                                                style={{ marginBottom: 8 }}
                                            >
                                                <option value="">+ Agregar otro agente</option>
                                                {availableAgents.map(a => (
                                                    <option key={a._id} value={a._id}>{a.name}</option>
                                                ))}
                                            </select>
                                            <button
                                                type="button" className="td-btn td-btn-soft full"
                                                onClick={() => handleSaveCollaborators([...collabs, { _id: collabToAdd }])}
                                                disabled={!collabToAdd || savingCollabs}
                                            >
                                                {savingCollabs ? <Spinner size="sm" animation="border" /> : <>{I.plus} Agregar</>}
                                            </button>
                                        </>
                                    ) : (
                                        <p style={{ fontSize: 11, color: 'var(--c-text-4)', fontStyle: 'italic', marginBottom: 0 }}>
                                            No hay más agentes en este departamento para sumar.
                                        </p>
                                    )}
                                    {ticket.assignedTo2?.name && !collabIds.includes(String(ticket.assignedTo2._id)) && (
                                        <p style={{ fontSize: 11, color: 'var(--c-text-4)', marginTop: 10, fontStyle: 'italic' }}>
                                            Legacy: <strong>{ticket.assignedTo2.name}</strong> figuraba como segundo agente.
                                        </p>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Acciones rápidas (staff) */}
                        {isStaff && (
                            <div className="td-card td-card-pad-sm">
                                <p className="td-label">Acciones</p>
                                <div className="td-action-list">
                                    {ticket.status !== 'closed' && ticket.priority !== 'urgent' && (
                                        <button
                                            type="button" className="td-action-row danger"
                                            onClick={handleEscalate} disabled={loadingEscalate}
                                        >
                                            {loadingEscalate ? <Spinner size="sm" animation="border" /> : I.arrowUp}
                                            <span>Escalar prioridad</span>
                                        </button>
                                    )}
                                    {ticket.status !== 'closed' && (
                                        <details style={{ width: '100%' }}>
                                            <summary style={{ listStyle: 'none', cursor: 'pointer' }}>
                                                <span className="td-action-row" style={{ pointerEvents: 'none' }}>
                                                    {I.move}<span>Mover a otro departamento</span>
                                                </span>
                                            </summary>
                                            <div style={{ padding: '6px 10px 4px' }}>
                                                <p style={{ fontSize: 11, color: 'var(--c-text-4)', margin: '0 0 6px' }}>
                                                    Actual: <strong>{ticket.department?.name || '—'}</strong>
                                                </p>
                                                <select
                                                    className="td-select"
                                                    value={moveDeptId}
                                                    onChange={e => setMoveDeptId(e.target.value)}
                                                    style={{ marginBottom: 8 }}
                                                >
                                                    <option value="">— Seleccionar —</option>
                                                    {departments.filter(d => d._id !== ticket.department?._id).map(d => (
                                                        <option key={d._id} value={d._id}>{d.name}</option>
                                                    ))}
                                                </select>
                                                <button
                                                    type="button" className="td-btn td-btn-soft full"
                                                    onClick={handleMoveDept}
                                                    disabled={!moveDeptId || loadingMoveDept}
                                                >
                                                    {loadingMoveDept ? <Spinner size="sm" animation="border" /> : 'Redirigir ticket'}
                                                </button>
                                            </div>
                                        </details>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Materiales / Fecha de entrega (staff) */}
                        {isStaff && showMaterialsBlock && (
                            <div className="td-card td-card-pad-sm">
                                <p className="td-label">Entrega</p>
                                <p style={{ fontSize: 11, color: 'var(--c-text-4)', margin: '0 0 6px' }}>Cantidad de materiales</p>
                                <input
                                    type="number" min={1}
                                    className="td-input"
                                    style={{ marginBottom: 8 }}
                                    value={materialsCount}
                                    onChange={e => setMaterialsCount(e.target.value)}
                                    placeholder="Núm. de materiales"
                                />
                                <button
                                    type="button" className="td-btn td-btn-soft full"
                                    style={{ marginBottom: 14 }}
                                    onClick={async () => {
                                        try {
                                            const val = materialsCount === '' ? null : Number(materialsCount);
                                            const res = await api.patch(`/tickets/${id}`, { materialsCount: val });
                                            syncTicketState(res.data);
                                            toast.success('Cantidad actualizada');
                                        } catch { toast.error('Error al actualizar'); }
                                    }}
                                >Guardar cantidad</button>

                                <p style={{ fontSize: 11, color: 'var(--c-text-4)', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {I.calendar} Fecha de entrega
                                </p>
                                <DeliveryCalendar
                                    deliveryDate={deliveryDateEdit}
                                    onSelect={d => setDeliveryDateEdit(d)}
                                />
                                <button
                                    type="button" className="td-btn td-btn-primary full"
                                    style={{ marginTop: 10 }}
                                    disabled={!deliveryDateEdit}
                                    onClick={async () => {
                                        try {
                                            const res = await api.patch(`/tickets/${id}`, {
                                                deliveryDate: deliveryDateEdit || null,
                                            });
                                            syncTicketState(res.data);
                                            toast.success('Fecha de entrega guardada');
                                        } catch { toast.error('Error al guardar'); }
                                    }}
                                >{I.check} Guardar fecha</button>
                            </div>
                        )}

                        {/* Autorización (staff) */}
                        {isStaff && (
                            <div className="td-card td-card-pad-sm">
                                <p className="td-label">Autorización</p>
                                <Form.Check
                                    type="switch"
                                    id="requires-auth-switch"
                                    label="Requiere autorización"
                                    checked={requiresAuthorization}
                                    onChange={e => setRequiresAuthorization(e.target.checked)}
                                    className="mb-3"
                                    style={{ fontSize: 13 }}
                                />
                                {requiresAuthorization && (
                                    <>
                                        <p style={{ fontSize: 11, color: 'var(--c-text-3)', fontWeight: 600, textTransform: 'uppercase', margin: '0 0 4px' }}>Principal</p>
                                        <select
                                            className="td-select"
                                            value={authorizerId}
                                            onChange={e => setAuthorizerId(e.target.value)}
                                            style={{ marginBottom: 8 }}
                                        >
                                            <option value="">— Seleccionar —</option>
                                            {authorizers.filter(a => a._id !== authorizer2Id).map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
                                        </select>
                                        <p style={{ fontSize: 11, color: 'var(--c-text-3)', fontWeight: 600, textTransform: 'uppercase', margin: '0 0 4px' }}>
                                            Segundo <span style={{ fontWeight: 400, textTransform: 'none' }}>(opcional)</span>
                                        </p>
                                        <select
                                            className="td-select"
                                            value={authorizer2Id}
                                            onChange={e => setAuthorizer2Id(e.target.value)}
                                            style={{ marginBottom: 8 }}
                                        >
                                            <option value="">— Ninguno —</option>
                                            {authorizers.filter(a => a._id !== authorizerId && a._id !== authorizer3Id).map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
                                        </select>
                                        <p style={{ fontSize: 11, color: 'var(--c-text-3)', fontWeight: 600, textTransform: 'uppercase', margin: '0 0 4px' }}>
                                            Tercero <span style={{ fontWeight: 400, textTransform: 'none' }}>(opcional)</span>
                                        </p>
                                        <select
                                            className="td-select"
                                            value={authorizer3Id}
                                            onChange={e => setAuthorizer3Id(e.target.value)}
                                            style={{ marginBottom: 12 }}
                                        >
                                            <option value="">— Ninguno —</option>
                                            {authorizers.filter(a => a._id !== authorizerId && a._id !== authorizer2Id).map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
                                        </select>
                                    </>
                                )}

                                {ticket.authorizationStatus !== 'none' && (
                                    <div className="td-auth-summary">
                                        <div className="row-item">
                                            <span style={{ fontWeight: 600 }}>Estado:</span>
                                            <AuthBadge status={ticket.authorizationStatus} />
                                        </div>
                                        {ticket.authorizer?.name && (
                                            <div className="row-item">
                                                <span style={{ fontWeight: 600 }}>{ticket.authorizer2 ? 'Aut. 1' : 'Autorizador'}:</span>
                                                <UserNameButton userId={ticket.authorizer._id} name={ticket.authorizer.name} onClick={setProfileUserId} />
                                                <AuthBadge status={auth1Status} />
                                                {ticket.authorizerDecidedAt && <span style={{ fontSize: 10 }}>{fmt(ticket.authorizerDecidedAt)}</span>}
                                            </div>
                                        )}
                                        {ticket.authorizer2?.name && (
                                            <div className="row-item">
                                                <span style={{ fontWeight: 600 }}>Aut. 2:</span>
                                                <UserNameButton userId={ticket.authorizer2._id} name={ticket.authorizer2.name} onClick={setProfileUserId} />
                                                <AuthBadge status={auth2Status} />
                                                {ticket.authorizer2DecidedAt && <span style={{ fontSize: 10 }}>{fmt(ticket.authorizer2DecidedAt)}</span>}
                                            </div>
                                        )}
                                        {ticket.authorizer3?.name && (
                                            <div className="row-item">
                                                <span style={{ fontWeight: 600 }}>Aut. 3:</span>
                                                <UserNameButton userId={ticket.authorizer3._id} name={ticket.authorizer3.name} onClick={setProfileUserId} />
                                                <AuthBadge status={auth3Status} />
                                                {ticket.authorizer3DecidedAt && <span style={{ fontSize: 10 }}>{fmt(ticket.authorizer3DecidedAt)}</span>}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <button
                                    type="button" className="td-btn td-btn-info full"
                                    onClick={handleSaveAuthorization} disabled={loadingAuth}
                                >
                                    {loadingAuth ? <Spinner size="sm" animation="border" /> : <>{I.shield} Guardar</>}
                                </button>
                            </div>
                        )}

                        {/* Panel del autorizador */}
                        {isMyAuthorization && (
                            <div className="td-card td-card-pad-sm td-auth-panel">
                                <p className="td-label" style={{ color: '#1d4ed8' }}>Tu decisión</p>

                                <div style={{ fontSize: 12, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ color: 'var(--c-text-3)' }}>General:</span>
                                    <AuthBadge status={ticket.authorizationStatus} />
                                </div>

                                {ticket.authorizer?.name && (
                                    <div className="td-auth-row">
                                        <span>{ticket.authorizer2 ? 'Aut. 1' : 'Autorizador'}: {nombreVisible(ticket.authorizer.name, user?.role)}</span>
                                        <AuthBadge status={auth1Status} />
                                    </div>
                                )}
                                {ticket.authorizer2?.name && (
                                    <div className="td-auth-row">
                                        <span>Aut. 2: {nombreVisible(ticket.authorizer2.name, user?.role)}</span>
                                        <AuthBadge status={auth2Status} />
                                    </div>
                                )}
                                {ticket.authorizer3?.name && (
                                    <div className="td-auth-row" style={{ marginBottom: 12 }}>
                                        <span>Aut. 3: {nombreVisible(ticket.authorizer3.name, user?.role)}</span>
                                        <AuthBadge status={auth3Status} />
                                    </div>
                                )}

                                {canDecide ? (
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button
                                            type="button" className="td-btn td-btn-success" style={{ flex: 1 }}
                                            onClick={() => handleAuthorize('approved')} disabled={loadingAuth}
                                        >
                                            {loadingAuth ? <Spinner size="sm" animation="border" /> : <>{I.check} Aprobar</>}
                                        </button>
                                        <button
                                            type="button" className="td-btn td-btn-danger" style={{ flex: 1 }}
                                            onClick={() => handleAuthorize('rejected')} disabled={loadingAuth}
                                        >
                                            {loadingAuth ? <Spinner size="sm" animation="border" /> : <>{I.x} Rechazar</>}
                                        </button>
                                    </div>
                                ) : (
                                    <p style={{ fontSize: 12, color: 'var(--c-text-2)', margin: 0 }}>
                                        {myStatus === 'approved'
                                            ? `✓ Aprobaste este ticket${myDecidedAt ? ` — ${fmt(myDecidedAt)}` : ''}`
                                            : myStatus === 'rejected'
                                            ? `✗ Rechazaste este ticket${myDecidedAt ? ` — ${fmt(myDecidedAt)}` : ''}`
                                            : 'La autorización ya fue procesada'}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* CC */}
                        {isStaff && (
                            <div className="td-card td-card-pad-sm">
                                <p className="td-label">Copia por correo (CC)</p>
                                <p style={{ fontSize: 11, color: 'var(--c-text-4)', margin: '0 0 10px' }}>
                                    Se notificará a estos correos cuando haya un comentario nuevo. Máx. 3.
                                </p>
                                {ccEmails.map((email, i) => (
                                    <div key={i} className="td-cc-item">
                                        <span className="email">{I.mail} {email}</span>
                                        <button type="button" className="remove" onClick={() => setCcEmails(ccEmails.filter((_, j) => j !== i))}>✕</button>
                                    </div>
                                ))}
                                {ccEmails.length < 3 && (
                                    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                                        <input
                                            type="email"
                                            className="td-input"
                                            placeholder="correo@ejemplo.com"
                                            value={ccInput}
                                            onChange={e => setCcInput(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    const v = ccInput.trim().toLowerCase();
                                                    if (v && !ccEmails.includes(v)) setCcEmails([...ccEmails, v]);
                                                    setCcInput('');
                                                }
                                            }}
                                        />
                                        <button
                                            type="button" className="td-btn td-btn-soft"
                                            style={{ whiteSpace: 'nowrap' }}
                                            onClick={() => {
                                                const v = ccInput.trim().toLowerCase();
                                                if (v && !ccEmails.includes(v)) setCcEmails([...ccEmails, v]);
                                                setCcInput('');
                                            }}
                                        >{I.plus}</button>
                                    </div>
                                )}
                                <button
                                    type="button" className="td-btn td-btn-info full"
                                    onClick={handleSaveCc} disabled={loadingCc}
                                >
                                    {loadingCc ? <Spinner size="sm" animation="border" /> : 'Guardar CC'}
                                </button>
                            </div>
                        )}

                        {/* Info read-only para no-staff */}
                        {!isStaff && !isMyAuthorization && (
                            <div className="td-card td-card-pad-sm">
                                <p className="td-label">Información</p>
                                <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <div>
                                        <span style={{ color: 'var(--c-text-4)', fontSize: 11, display: 'block', marginBottom: 4 }}>Estado</span>
                                        <StatusPill status={ticket.status} />
                                    </div>
                                    <div>
                                        <span style={{ color: 'var(--c-text-4)', fontSize: 11, display: 'block', marginBottom: 4 }}>Asignado a</span>
                                        <span style={{ fontWeight: 500 }}>
                                            <UserNameButton
                                                userId={ticket.assignedTo?._id}
                                                name={nombreVisible(ticket.assignedTo?.name, user?.role) || '—'}
                                                onClick={setProfileUserId}
                                            />
                                        </span>
                                        {ticket.assignedTo2?.name && (
                                            <span style={{ fontWeight: 500, display: 'block', fontSize: 12, color: 'var(--c-text-3)' }}>
                                                + <UserNameButton
                                                    userId={ticket.assignedTo2?._id}
                                                    name={nombreVisible(ticket.assignedTo2.name, user?.role)}
                                                    onClick={setProfileUserId}
                                                />
                                            </span>
                                        )}
                                    </div>
                                    {ticket.requiresAuthorization && (
                                        <div>
                                            <span style={{ color: 'var(--c-text-4)', fontSize: 11, display: 'block', marginBottom: 4 }}>Autorización</span>
                                            <AuthBadge status={ticket.authorizationStatus} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Drawer tab flotante */}
            <button
                type="button"
                className={`td-drawer-tab${showDrawer ? ' open' : ''}`}
                onClick={() => setShowDrawer(v => !v)}
                style={{ right: showDrawer ? 320 : 0 }}
            >
                {showDrawer ? '▶' : '◀ Tickets'}
            </button>

            {/* Fondo para cerrar el cajón tocando fuera (solo móvil) */}
            <div className={`td-drawer-backdrop${showDrawer ? ' show' : ''}`} onClick={() => setShowDrawer(false)} />

            {/* Drawer panel */}
            <div className="td-drawer" style={{ transform: showDrawer ? 'translateX(0)' : 'translateX(100%)' }}>
                <div className="td-drawer-header">
                    <div>
                        <h6>Tickets abiertos</h6>
                        <p className="count">{drawerTickets.length} resultados</p>
                    </div>
                    <button type="button" className="td-drawer-close" onClick={() => setShowDrawer(false)}>{I.x}</button>
                </div>
                <div className="td-drawer-body">
                    {loadingDrawer ? (
                        <div style={{ textAlign: 'center', padding: 32 }}><Spinner size="sm" animation="border" variant="primary" /></div>
                    ) : drawerTickets.length === 0 ? (
                        <p style={{ textAlign: 'center', color: 'var(--c-text-4)', fontSize: 13, padding: '32px 0' }}>No hay tickets abiertos</p>
                    ) : drawerTickets.map(t => {
                        const isCurrent = String(t._id) === String(id);
                        const s = STATUS_CFG[t.status] || { color: 'var(--c-text-3)', label: t.status };
                        return (
                            <div
                                key={t._id}
                                className={`td-drawer-item${isCurrent ? ' current' : ''}`}
                                onClick={() => navigate(`/tickets/${t._id}`)}
                                style={{ borderLeft: `3px solid ${s.color}` }}
                            >
                                <div className="top">
                                    <span className="folio">#{t.folio || '—'}</span>
                                    <span style={{ fontSize: 10, color: s.color, fontWeight: 600 }}>{s.label}</span>
                                </div>
                                <p className="title-text">{t.title}</p>
                                {t.department?.name && <p className="dept">{I.building} {t.department.name}</p>}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modal CSAT */}
            {showCsatModal && ticket && (
                <Modal
                    show
                    centered
                    backdrop="static"
                    onHide={() => {
                        sessionStorage.setItem(`csat_dismissed_${ticket._id}`, '1');
                        setShowCsatModal(false);
                    }}
                >
                    <Modal.Header closeButton style={{ borderBottom: '1px solid var(--c-border)' }}>
                        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
                            ¿Cómo calificarías la atención recibida?
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: '24px' }}>
                        <p style={{ fontSize: 13, color: 'var(--c-text-3)', marginBottom: 16 }}>
                            Tu opinión nos ayuda a mejorar el servicio. Solo toma un momento.
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                            <StarRating value={csatRating} onChange={setCsatRating} size={40} />
                        </div>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Selecciona un comentario rápido</Form.Label>
                            <Form.Select
                                size="sm"
                                value={csatPreset}
                                onChange={e => { setCsatPreset(e.target.value); setCsatComment(''); }}
                                style={{ borderRadius: 8, fontSize: 13 }}
                            >
                                <option value="">— Elige una opción —</option>
                                <option value="Excelente atención, muy satisfecho con la solución">Excelente atención, muy satisfecho</option>
                                <option value="El problema fue resuelto de forma rápida y eficiente">Rápido y eficiente</option>
                                <option value="Buena comunicación y seguimiento oportuno">Buena comunicación y seguimiento</option>
                                <option value="La solución fue clara y precisa">Solución clara y precisa</option>
                                <option value="Podría mejorar el tiempo de respuesta">Podría mejorar el tiempo de respuesta</option>
                                <option value="No quedé del todo satisfecho con la resolución">No quedé del todo satisfecho</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                                ¿Algo más que quieras agregar?{' '}
                                <span style={{ fontWeight: 400, color: 'var(--c-text-4)' }}>(opcional)</span>
                            </Form.Label>
                            <Form.Control
                                as="textarea" rows={2}
                                value={csatComment}
                                onChange={e => { setCsatComment(e.target.value); if (e.target.value) setCsatPreset(''); }}
                                placeholder="Escribe tu comentario aquí…"
                                style={{ borderRadius: 8, fontSize: 13 }}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: '1px solid var(--c-border)' }}>
                        <Button
                            variant="outline-secondary" size="sm" style={{ borderRadius: 8 }}
                            onClick={() => {
                                sessionStorage.setItem(`csat_dismissed_${ticket._id}`, '1');
                                setShowCsatModal(false);
                            }}
                        >
                            Calificar después
                        </Button>
                        <Button
                            variant="warning" size="sm" style={{ borderRadius: 8, fontWeight: 600 }}
                            disabled={!csatRating || loadingCsat}
                            onClick={() => handleSubmitCsat(true)}
                        >
                            {loadingCsat ? <Spinner size="sm" animation="border" /> : <>⭐ Enviar calificación</>}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Modal de borrado de comentario */}
            <Modal
                show={!!deleteCommentId}
                onHide={() => { if (!loadingDelete) { setDeleteCommentId(null); setDeleteReason(''); } }}
                centered size="sm"
            >
                <Modal.Header closeButton style={{ borderBottom: '1px solid var(--c-border)' }}>
                    <Modal.Title style={{ fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {I.trash} Borrar comentario
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px' }}>
                    <p style={{ fontSize: 13, color: 'var(--c-text-2)', marginBottom: 12 }}>
                        Esta acción oculta el comentario. Queda archivado con tu nombre y la razón para fines de auditoría.
                    </p>
                    <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
                            Motivo del borrado <span style={{ color: '#dc2626' }}>*</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea" rows={3}
                            value={deleteReason}
                            onChange={e => setDeleteReason(e.target.value)}
                            placeholder="Ej: contenía información sensible publicada por error"
                            style={{ borderRadius: 8, fontSize: 13 }}
                            autoFocus disabled={loadingDelete}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid var(--c-border)' }}>
                    <Button
                        variant="outline-secondary" size="sm" style={{ borderRadius: 8 }}
                        onClick={() => { setDeleteCommentId(null); setDeleteReason(''); }}
                        disabled={loadingDelete}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="danger" size="sm" style={{ borderRadius: 8, fontWeight: 600 }}
                        onClick={handleConfirmDeleteComment}
                        disabled={loadingDelete || !deleteReason.trim()}
                    >
                        {loadingDelete ? <Spinner size="sm" animation="border" /> : 'Confirmar borrado'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de perfil del colaborador */}
            <UserProfileModal
                userId={profileUserId}
                onClose={() => setProfileUserId(null)}
            />
        </>
    );
}
